// Force recompile - updated at $(date)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import SuccessPopup from '../shared/SuccessPopup';
import AutoSaveToast from '../shared/AutoSaveToast';
import { combineFormInstances, splitFormDataByInstance, extractStudentInfo } from '../../utils/formCombiner';
import { confirmUngeocodedMapAddressIfNeeded } from '../../utils/mapSubmitGuard';
import MigrantQuestionnaire, {
  missingMigrantAnswers, emptyMigrantAnswers, MIGRANT_FIELD_LABELS, migrantTotalRequired
} from './MigrantQuestionnaire';
import { mapFieldHasPlacedMarker } from '../../utils/mapFieldValidation';
import { syncSharedFields } from '../../utils/sharedFieldMapper';
import { extractYMD } from '../../utils/dateOnly';
import {
  saveLocalDraft, readLocalDraft, clearLocalDraft, describeAge, countRecoverableChanges
} from '../../utils/localDraftBackup';
import {
  buildNewStudentAutofillProfile,
  normalizeHouseholdProfileData,
  resolveStudentIntakeFieldValue
} from '../../utils/autofillProfileResolver';
import '../superadmin/AddDistrict.css';
import './NewStudentForm.css';

const isMeaningfulFormValue = (v) =>
  v !== null &&
  v !== undefined &&
  v !== '' &&
  v !== 'Please select an option' &&
  !(Array.isArray(v) && v.length === 0);

const isHelperFormInstanceName = (instance) =>
  (instance?.instance_name || instance?.master_form_name || '').toLowerCase().includes('helper');

const parsePrefixedFieldName = (prefixedFieldName) => {
  const parts = String(prefixedFieldName || '').split('_');
  if (parts.length < 2) return null;
  return {
    instanceId: parts[0],
    originalFieldName: parts.slice(1).join('_')
  };
};

const isAutofillValueEmpty = (v) =>
  v === null || v === undefined || v === '' || v === 'Please select an option' || (Array.isArray(v) && v.length === 0);

const looksLikeStudentIntakeField = (fieldName) =>
  /(^student_name$)|(^legal_name_)|(^student_)|(^first_name$)|(^last_name$)|(^middle_name$)|(^date_of_birth$)|(^dob$)|(^birth_date$)|(^age$)|(^grade$)|(^gender$)|(^sex$)|(^race$)|(^ethnicity$)|(^ethnicity_race$)|(^nickname$)|(^previous_last_name$)/i
    .test(String(fieldName || ''));

const normalizeSubmissionDataObject = (raw) => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return {};
    }
  }
  return {};
};

const sanitizeForJsonObject = (raw) => {
  const seen = new WeakSet();
  const replacer = (_key, value) => {
    if (typeof value === 'function' || typeof value === 'symbol' || value === undefined) {
      return undefined;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (value && typeof value === 'object') {
      if (seen.has(value)) return undefined;
      seen.add(value);
    }
    return value;
  };

  try {
    const normalized = normalizeSubmissionDataObject(raw);
    const serialized = JSON.stringify(normalized, replacer);
    if (!serialized) return {};
    const parsed = JSON.parse(serialized);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const safeNormalizeHouseholdProfileData = (raw = {}) => {
  if (typeof normalizeHouseholdProfileData === 'function') {
    try {
      const normalized = normalizeHouseholdProfileData(raw || {});
      if (normalized && typeof normalized === 'object' && !Array.isArray(normalized)) {
        return normalized;
      }
    } catch (error) {
      console.warn('normalizeHouseholdProfileData failed, using raw household data:', error?.message || error);
    }
  }
  return normalizeSubmissionDataObject(raw);
};

const safeBuildNewStudentAutofillProfile = (args = {}) => {
  if (typeof buildNewStudentAutofillProfile === 'function') {
    try {
      const profile = buildNewStudentAutofillProfile(args);
      if (profile && typeof profile === 'object' && !Array.isArray(profile)) {
        return profile;
      }
    } catch (error) {
      console.warn('buildNewStudentAutofillProfile failed, using fallback profile merge:', error?.message || error);
    }
  }

  const safeSavedSubmissionData = normalizeSubmissionDataObject(args.savedSubmissionData);
  return {
    ...safeNormalizeHouseholdProfileData(args.householdInformation || {}),
    ...normalizeSubmissionDataObject(args.fallbackProfile || {}),
    ...safeSavedSubmissionData
  };
};

const AUTOFILL_ALIAS_MAP = {
  legal_name_first: ['first_name', 'student_first_name', 'student_legal_first_name'],
  legal_name_last: ['last_name', 'student_last_name', 'student_legal_last_name'],
  legal_name_middle: ['middle_name', 'student_middle_name', 'student_legal_middle_name'],
  date_of_birth: ['student_birth_date'],
  grade: ['student_grade', 'grade_level'],
  primary_household_phone: ['primary_phone', 'primary_parent_phone', 'parent_phone', 'parent_guardian_phone'],
  home_street_address: ['primary_address_street'],
  home_city: ['primary_address_city'],
  home_state: ['primary_address_state'],
  home_zip_code: ['primary_address_zip'],
  home_county: ['home_county', 'primary_address_county', 'address_county'],
  primary_mailing_address_street: ['mailing_address_street', 'primary_mailing_street', 'mailing_street_address'],
  primary_mailing_address_city: ['mailing_address_city', 'primary_mailing_city', 'mailing_city'],
  primary_mailing_address_state: ['mailing_address_state', 'primary_mailing_state', 'mailing_state'],
  primary_mailing_address_zip: ['mailing_address_zip', 'primary_mailing_zip', 'mailing_zip_code'],
  primary_mailing_address_county: ['mailing_address_county', 'primary_mailing_county', 'mailing_county'],
  primary_parent_first_name: ['parent_first_name'],
  primary_parent_last_name: ['parent_last_name'],
  primary_parent_cell_phone: ['parent_guardian_phone'],
  primary_parent_home_phone: ['parent_guardian_phone'],
  gender: ['sex']
};

const getStudentIntakeSourceObject = (studentIntake) => (
  studentIntake?.student_intake ||
  studentIntake?.studentIntake ||
  studentIntake?.student ||
  studentIntake?.data ||
  studentIntake ||
  {}
);

const firstNonEmptyStudentValue = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (!isAutofillValueEmpty(value)) {
      return value;
    }
  }
  return undefined;
};

const resolveStudentIntakeFieldValueFallback = (fieldName, studentIntake) => {
  if (!fieldName || !studentIntake) return undefined;
  const source = getStudentIntakeSourceObject(studentIntake);
  const normalizedFieldName = String(fieldName).trim().toLowerCase();

  const firstName = firstNonEmptyStudentValue(source, ['first_name', 'firstName', 'student_first_name', 'studentFirstName', 'legal_name_first', 'legalNameFirst']);
  const lastName = firstNonEmptyStudentValue(source, ['last_name', 'lastName', 'student_last_name', 'studentLastName', 'legal_name_last', 'legalNameLast']);
  const middleName = firstNonEmptyStudentValue(source, ['middle_name', 'middleName', 'student_middle_name', 'studentMiddleName', 'legal_name_middle', 'legalNameMiddle']);
  const dateOfBirth = firstNonEmptyStudentValue(source, ['date_of_birth', 'dateOfBirth', 'dob', 'student_date_of_birth', 'studentDateOfBirth', 'student_birth_date', 'studentBirthDate', 'birth_date', 'birthDate']);
  const age = firstNonEmptyStudentValue(source, ['age', 'student_age', 'studentAge']);
  const grade = firstNonEmptyStudentValue(source, ['grade', 'grade_level', 'gradeLevel', 'student_grade', 'studentGrade']);
  const gender = firstNonEmptyStudentValue(source, ['gender', 'sex', 'student_gender', 'studentGender']);
  const race = firstNonEmptyStudentValue(source, ['race', 'ethnicity_race', 'ethnicityRace', 'student_race', 'studentRace']);
  const ethnicity = firstNonEmptyStudentValue(source, ['ethnicity', 'student_ethnicity', 'studentEthnicity']);
  const nickname = firstNonEmptyStudentValue(source, ['nickname', 'student_nickname', 'studentNickname']);
  const previousLastName = firstNonEmptyStudentValue(source, ['previous_last_name', 'previousLastName', 'student_previous_last_name', 'studentPreviousLastName']);

  if (normalizedFieldName === 'student_name') {
    const fullName = `${String(firstName || '').trim()} ${String(lastName || '').trim()}`.trim();
    return fullName || undefined;
  }
  if (['first_name', 'student_first_name', 'legal_name_first', 'student_legal_first_name'].includes(normalizedFieldName)) return firstName;
  if (['last_name', 'student_last_name', 'legal_name_last', 'student_legal_last_name'].includes(normalizedFieldName)) return lastName;
  if (['middle_name', 'student_middle_name', 'legal_name_middle', 'student_legal_middle_name'].includes(normalizedFieldName)) return middleName;
  if (['date_of_birth', 'student_date_of_birth', 'student_birth_date', 'birth_date', 'dob'].includes(normalizedFieldName)) {
    if (isAutofillValueEmpty(dateOfBirth)) return undefined;
    return String(dateOfBirth).includes('T') ? String(dateOfBirth).split('T')[0] : dateOfBirth;
  }
  if (['age', 'student_age'].includes(normalizedFieldName)) return age;
  if (['grade', 'student_grade', 'grade_level'].includes(normalizedFieldName)) return grade;
  if (['gender', 'student_gender', 'sex'].includes(normalizedFieldName)) return gender;
  if (['race', 'ethnicity_race'].includes(normalizedFieldName)) return race;
  if (['ethnicity', 'student_ethnicity'].includes(normalizedFieldName)) return ethnicity;
  if (normalizedFieldName === 'nickname') return nickname;
  if (normalizedFieldName === 'previous_last_name') return previousLastName;

  return undefined;
};

const safeResolveStudentIntakeFieldValue = (fieldName, studentIntake) => {
  if (typeof resolveStudentIntakeFieldValue === 'function') {
    try {
      const resolved = resolveStudentIntakeFieldValue(fieldName, studentIntake);
      if (!isAutofillValueEmpty(resolved)) {
        return resolved;
      }
    } catch (error) {
      console.warn('resolveStudentIntakeFieldValue failed, using local fallback:', error?.message || error);
    }
  }
  return resolveStudentIntakeFieldValueFallback(fieldName, studentIntake);
};

const buildFieldDefinitionMap = (combinedFormDefinition) => {
  const map = {};
  if (!combinedFormDefinition?.sections || !Array.isArray(combinedFormDefinition.sections)) {
    return map;
  }
  combinedFormDefinition.sections.forEach((section) => {
    (section?.fields || []).forEach((field) => {
      if (field?.name) {
        map[field.name] = field;
      }
    });
  });
  return map;
};

const normalizeValuesToFieldOptions = (rawValues, options = []) => {
  if (!Array.isArray(options) || options.length === 0) return rawValues;
  const values = Array.isArray(rawValues) ? rawValues : [rawValues];
  const matched = [];

  values.forEach((rawValue) => {
    if (isAutofillValueEmpty(rawValue)) return;
    const value = String(rawValue).trim();
    if (!value) return;
    const exact = options.find((option) => option === value);
    if (exact) {
      if (!matched.includes(exact)) matched.push(exact);
      return;
    }

    const lowerValue = value.toLowerCase();
    const fuzzy = options.find((option) => {
      const lowerOption = String(option).toLowerCase();
      return lowerOption === lowerValue || lowerOption.includes(lowerValue) || lowerValue.includes(lowerOption);
    });
    if (fuzzy && !matched.includes(fuzzy)) {
      matched.push(fuzzy);
    }
  });

  return matched.length > 0 ? matched : values.filter((v) => !isAutofillValueEmpty(v)).map((v) => String(v).trim());
};

const normalizeStudentAutofillValueForField = ({ originalFieldName, intakeValue, fieldDefinition, studentIntake }) => {
  if (isAutofillValueEmpty(intakeValue)) return intakeValue;

  const normalizedFieldName = String(originalFieldName || '').toLowerCase();

  // Combined ethnicity/race fields are typically checkbox groups; pull both values.
  if (normalizedFieldName === 'ethnicity_race') {
    const source = getStudentIntakeSourceObject(studentIntake);
    const race = firstNonEmptyStudentValue(source, ['race', 'ethnicity_race', 'ethnicityRace', 'student_race', 'studentRace']);
    const ethnicity = firstNonEmptyStudentValue(source, ['ethnicity', 'student_ethnicity', 'studentEthnicity']);
    const combinedValues = [race, ethnicity].filter((v) => !isAutofillValueEmpty(v));

    if (fieldDefinition?.type === 'checkbox' && Array.isArray(fieldDefinition?.options)) {
      return normalizeValuesToFieldOptions(
        combinedValues.length > 0 ? combinedValues : intakeValue,
        fieldDefinition.options
      );
    }

    if (combinedValues.length > 1) {
      return combinedValues.join(', ');
    }
    if (combinedValues.length === 1) {
      return combinedValues[0];
    }
  }

  // Multi-select checkboxes must be arrays for the renderer to show checked values.
  if (fieldDefinition?.type === 'checkbox' && Array.isArray(fieldDefinition?.options)) {
    if (Array.isArray(intakeValue)) {
      return normalizeValuesToFieldOptions(intakeValue, fieldDefinition.options);
    }
    if (typeof intakeValue === 'string') {
      const splitValues = intakeValue
        .split(/[|,]/)
        .map((v) => v.trim())
        .filter(Boolean);
      return normalizeValuesToFieldOptions(splitValues.length > 0 ? splitValues : [intakeValue], fieldDefinition.options);
    }
    return normalizeValuesToFieldOptions([intakeValue], fieldDefinition.options);
  }

  return intakeValue;
};

const shouldBackfillStudentFieldValue = (currentValue, fieldDefinition) => {
  if (isAutofillValueEmpty(currentValue)) return true;

  // Legacy bad writes sometimes stored checkbox multi-selects as strings,
  // which render visually empty; treat those as fillable.
  if (fieldDefinition?.type === 'checkbox' && Array.isArray(fieldDefinition?.options) && !Array.isArray(currentValue)) {
    return true;
  }

  return false;
};

const buildStudentIntakeBackfillResult = (values, studentIntake, combinedFormDefinition) => {
  if (!studentIntake || !values || typeof values !== 'object') {
    return {
      nextValues: values,
      changed: false,
      scannedStudentFieldCount: 0,
      resolvedStudentFieldCount: 0,
      unresolvedDistinctFieldNames: [],
      appliedFieldKeys: []
    };
  }

  let changed = false;
  const next = { ...values };
  const fieldDefinitionMap = buildFieldDefinitionMap(combinedFormDefinition);
  const unresolvedStudentFieldNames = new Set();
  const appliedFieldKeys = [];
  let scannedStudentFieldCount = 0;
  let resolvedStudentFieldCount = 0;
  Object.keys(next).forEach((fieldKey) => {
    const parsed = parsePrefixedFieldName(fieldKey);
    const originalFieldName = parsed ? parsed.originalFieldName : fieldKey;
    const isStudentLikeField = looksLikeStudentIntakeField(originalFieldName);
    if (isStudentLikeField) {
      scannedStudentFieldCount += 1;
    }
    const fieldDefinition = fieldDefinitionMap[fieldKey];
    const rawIntakeValue = safeResolveStudentIntakeFieldValue(originalFieldName, studentIntake);
    const intakeValue = normalizeStudentAutofillValueForField({
      originalFieldName,
      intakeValue: rawIntakeValue,
      fieldDefinition,
      studentIntake
    });
    const hasResolvedIntakeValue = !isAutofillValueEmpty(intakeValue);
    if (isStudentLikeField && hasResolvedIntakeValue) {
      resolvedStudentFieldCount += 1;
    }
    if (isStudentLikeField && !hasResolvedIntakeValue) {
      unresolvedStudentFieldNames.add(originalFieldName);
    }
    if (shouldBackfillStudentFieldValue(next[fieldKey], fieldDefinition) && !isAutofillValueEmpty(intakeValue)) {
      next[fieldKey] = intakeValue;
      changed = true;
      appliedFieldKeys.push(fieldKey);
    }
  });

  return {
    nextValues: changed ? next : values,
    changed,
    scannedStudentFieldCount,
    resolvedStudentFieldCount,
    unresolvedDistinctFieldNames: Array.from(unresolvedStudentFieldNames).sort(),
    appliedFieldKeys
  };
};

const NewStudentForm = () => {
  const navigate = useNavigate();
  const { studentIntakeId } = useParams();
  const [studentIntake, setStudentIntake] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [combinedFormDefinition, setCombinedFormDefinition] = useState(null);
  const [formInstanceMap, setFormInstanceMap] = useState({});
  const [formInstances, setFormInstances] = useState([]);
  const [allFormInstances, setAllFormInstances] = useState([]); // Store all instances before filtering
  const [formValues, setFormValues] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [savingProgress, setSavingProgress] = useState(false);
  const [saveFeedbackModal, setSaveFeedbackModal] = useState(null);
  const [autoSaveToast, setAutoSaveToast] = useState(null);
  /**
   * The autosave notice is shown ONCE per visit. It used to reappear after every
   * automatic save, so dismissing it achieved nothing - and on a form this long there
   * are a lot of saves. One notice tells the parent saving is automatic; after that,
   * silence is the correct feedback, and "Save Progress" still confirms explicitly.
   */
  const autoSaveNoticeShownRef = useRef(false);
  // A local mirror of what has been typed, in case a save never reaches the server.
  const [recoverableDraft, setRecoverableDraft] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [autofillEnabled, setAutofillEnabled] = useState({}); // Track autofill enabled/disabled per form instance
  const [scrollPosition, setScrollPosition] = useState(0);
  // DESE Title I.C questionnaire, completed alongside enrollment: it is state-required
  // for every student, and doing it here means the household block autofills instead of
  // the parent retyping it on a separate screen.
  const [migrantAnswers, setMigrantAnswers] = useState(emptyMigrantAnswers);
  const [migrantLanguage, setMigrantLanguage] = useState('en');
  const [migrantError, setMigrantError] = useState('');
  const [migrantAlreadyOnFile, setMigrantAlreadyOnFile] = useState(false);
  /**
   * How much of the bottom bar is showing: 'min' | 'default' | 'tall'.
   *
   * With a long list of outstanding fields the bar was taking half the screen on a phone.
   * Collapsing hides only the list - the progress row and the Save/Submit buttons stay
   * put, because a parent who has collapsed the bar must still be able to submit.
   *
   * Remembered for the session so it does not spring back open on every re-render.
   */
  const [barState, setBarState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('nsf_bar_state');
      return ['min', 'default', 'tall'].includes(saved) ? saved : 'default';
    } catch (e) {
      return 'default';
    }
  });
  const setBar = useCallback((next) => {
    setBarState(next);
    try { sessionStorage.setItem('nsf_bar_state', next); } catch (e) { /* private mode */ }
  }, []);
  const dragStartRef = useRef(null);

  // Pointer events rather than touch events, so a swipe on a phone and a drag with a
  // mouse are the same code path.
  const onGrabDown = useCallback((e) => {
    dragStartRef.current = { y: e.clientY, state: barState };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* not critical */ }
  }, [barState]);

  const onGrabUp = useCallback((e) => {
    const start = dragStartRef.current;
    dragStartRef.current = null;
    if (!start) return;
    const dy = e.clientY - start.y;
    const SWIPE = 30; // shorter than a scroll flick, long enough not to fire on a tap
    if (dy > SWIPE) {
      setBar(start.state === 'tall' ? 'default' : 'min');
    } else if (dy < -SWIPE) {
      setBar(start.state === 'min' ? 'default' : 'tall');
    } else {
      // A tap toggles rather than doing nothing, which is what most people try first.
      setBar(start.state === 'min' ? 'default' : 'min');
    }
  }, [setBar]);
  const [siblingAutofillMessage, setSiblingAutofillMessage] = useState('');
  const [autofillProfileData, setAutofillProfileData] = useState({}); // Canonical parent-level autofill profile
  const [deletedAuthorizedPersons, setDeletedAuthorizedPersons] = useState(new Set()); // Track manually deleted authorized persons
  const [manuallyChangedHelperFields, setManuallyChangedHelperFields] = useState(new Set()); // Track manually changed helper form fields
  const [manuallyClearedAutofillFields, setManuallyClearedAutofillFields] = useState(new Set()); // Prevent autofill from re-populating user-cleared fields


  // Helper function to check if a form instance gets autofilled from helper form
  const isFormAutofilled = (instance) => {
    const formName = (instance.instance_name || instance.master_form_name || '').toLowerCase();
    return (
      formName.includes('acceptable use agreement') ||
      formName.includes('primary parent household') ||
      formName.includes('new student') ||
      formName.includes('residency') ||
      formName.includes('emergency') ||
      formName.includes('consent') ||
      formName.includes('record')
    );
  };
  const [helperFormData, setHelperFormData] = useState({}); // Store helper form field values

  useEffect(() => {
    helperFormDataRef.current = helperFormData;
  }, [helperFormData]);

  const [householdInformation, setHouseholdInformation] = useState({}); // Store household information for autofill
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [hasUserStartedFilling, setHasUserStartedFilling] = useState(false);
  const [hasRunInitialAutofill, setHasRunInitialAutofill] = useState(false);
  const previousFormValuesRef = useRef({});
  const siblingsAutofillRef = useRef(null);
  const latestFormValuesRef = useRef(null); // Captures latest values from handleFormChange before state commits
  const helperFormDataRef = useRef({}); // Mirrors helperFormData for synchronous merge in handleFormChange (avoids stale grade on ethnicity updates)
  const studentBackfillAppliedForRef = useRef(null);
  const formInstancesInitializedForRef = useRef(null);

  // Track which helper form fields came from student intake data
  const [fieldsFromStudentIntake, setFieldsFromStudentIntake] = useState(new Set());

  // Function to determine if a field should be read-only (came from student intake)
  const isFieldReadOnly = (fullFieldName, fieldName) => {
    // Extract the simple field name from the full field name
    // Format is: "uuid_fieldname" - we want just "fieldname"
    const simpleFieldName = fullFieldName.split('_').slice(1).join('_');

    // Check if this field came from student intake data
    return fieldsFromStudentIntake.has(simpleFieldName);
  };




  // Load household information for reuse across students
  const loadHouseholdInformation = async () => {
    try {
      const response = await axios.get('/api/form-submissions/household-information');
      const householdData = safeNormalizeHouseholdProfileData(response.data || {});

      // Store household information for autofill
      setHouseholdInformation(householdData);
      setAutofillProfileData(householdData);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Household information not available yet - using empty data');
      }
      // Reset household information on error
      setHouseholdInformation({});
      setAutofillProfileData({});
    }
  };

  const fetchStudentIntake = useCallback(async () => {
    try {
      const response = await axios.get(`/api/student-intake/my-intake/${studentIntakeId}`);
      setStudentIntake(response.data);
    } catch (error) {
      console.error('Failed to fetch student intake:', error);
      console.error('Error details:', error.response?.data);
      setError('Failed to load student information');
    }
  }, [studentIntakeId]);

  // Fetch student intake data if studentIntakeId is provided
  useEffect(() => {
    const loadSiblings = async () => {
      try {
        const response = await axios.get('/api/student-intake/my-intake');
        const allStudents = response.data;
        const siblingsList = allStudents.filter(student => student.id !== studentIntakeId);
        setSiblings(siblingsList);
      } catch (error) {
        console.error('Failed to fetch siblings:', error);
      }
    };

    if (studentIntakeId) {
      // Check if this student was already submitted (prevent showing empty form after refresh)
      if (sessionStorage.getItem(`student_submitted_${studentIntakeId}`) === 'true') {
        navigate('/parent/students');
        return;
      }

      // Clear existing form data when switching students
      setFormValues({});
      latestFormValuesRef.current = null;
      helperFormDataRef.current = {};
      setHelperFormData({});
      setHouseholdInformation({});
      setAutofillProfileData({});
      setFieldsFromStudentIntake(new Set());
      setStudentIntake(null);
      setHasRunInitialAutofill(false);
      setManuallyChangedHelperFields(new Set());
      setHasUserStartedFilling(false);
      setLastSavedTime(null);
      setValidationErrors({});
      setError('');
      setSuccess(false);
      setLoading(true);
      fetchStudentIntake();
      loadSiblings();
      loadHouseholdInformation();
    } else {
      // Clear data for generic new student form
      setFormValues({});
      latestFormValuesRef.current = null;
      helperFormDataRef.current = {};
      setHelperFormData({});
      setHouseholdInformation({});
      setAutofillProfileData({});
      setStudentIntake(null);
      setHasRunInitialAutofill(false);
      setManuallyChangedHelperFields(new Set());
      setHasUserStartedFilling(false);
      setLastSavedTime(null);
      setValidationErrors({});
      setError('');
      setSuccess(false);
      loadSiblings();
      loadHouseholdInformation();
    }
    siblingsAutofillRef.current = null;
  }, [studentIntakeId, navigate, fetchStudentIntake]);

  // Rolled-forward students use Returning Student for the new year, not the new-student packet again.
  // Does this student already have a questionnaire? If so we show a note instead of
  // asking again, and submit skips it.
  useEffect(() => {
    let cancelled = false;
    if (!studentIntakeId) return undefined;
    (async () => {
      try {
        const res = await axios.get('/api/migrant-questionnaire/status');
        const match = (res.data.students || []).find((x) => x.studentIntakeId === studentIntakeId);
        if (!cancelled) setMigrantAlreadyOnFile(!!(match && match.submissionId));
      } catch (e) {
        // Non-fatal: fall through and let the parent fill it in.
      }
    })();
    return () => { cancelled = true; };
  }, [studentIntakeId]);

  useEffect(() => {
    if (!studentIntakeId || !studentIntake) return;
    if (studentIntake.rolled_from_intake_id && studentIntake.form_status === 'none') {
      navigate(`/parent/returning-student-enrollment/${studentIntakeId}`, { replace: true });
    }
  }, [studentIntakeId, studentIntake, navigate]);

  const loadSavedProgress = useCallback(async () => {
    try {
      const progressResponse = await axios.get(`/api/form-submissions/progress?student_intake_id=${studentIntakeId}&form_type=new_student`);


      const allFormData = Array.isArray(progressResponse.data) ? progressResponse.data : [];

      // Find progress for the combined form (null form_instance_id)
      const combinedFormProgress = allFormData.find(progress =>
        progress.form_instance_id === null && !progress.is_complete
      );

      // Essential debug logging

      // Build a source-agnostic autofill profile: household first, legacy helper in progress fallback.
      const hasSavedProgress = !hasUserStartedFilling &&
                               !!combinedFormProgress &&
                               !!combinedFormProgress.submission_data &&
                               Object.keys(normalizeSubmissionDataObject(combinedFormProgress.submission_data)).length > 0;

      const safeSavedSubmissionData = hasSavedProgress
        ? normalizeSubmissionDataObject(combinedFormProgress.submission_data)
        : {};

      const helperData = safeBuildNewStudentAutofillProfile({
        householdInformation,
        fallbackProfile: autofillProfileData,
        studentIntake,
        savedSubmissionData: safeSavedSubmissionData,
        allFormInstances
      });


      // Load combined form progress data for new/incomplete submissions
      // hasSavedProgress was already calculated above

      // Simplified approach: Always initialize helper form with available data
      // If there's saved progress, extract helper data from it, otherwise use the data we prepared
      if (hasSavedProgress) {
        const savedData = safeSavedSubmissionData;

        // Extract helper form data from saved progress (overrides parent data)
        Object.keys(savedData).forEach(prefixedFieldName => {
          const parts = prefixedFieldName.split('_');
          if (parts.length >= 2) {
            const instanceId = parts[0];
            const originalFieldName = parts.slice(1).join('_');

            const instance = allFormInstances.find(inst => inst.id === instanceId);
            if (instance) {
              const instanceName = instance.instance_name || instance.master_form_name || '';
              if (instanceName.includes('Helper Form')) {
                const value = savedData[prefixedFieldName];
                if (value !== null && value !== undefined && value !== '') {
                  if (originalFieldName === 'primary_parent_phone') {
                    helperData.primary_parent_cell_phone = value;
                  } else {
                    helperData[originalFieldName] = value;
                  }
                }
              }
            }
          }
        });

        // Load saved data since user hasn't started filling yet
        setFormValues(savedData);
        previousFormValuesRef.current = savedData; // Initialize ref
        latestFormValuesRef.current = null; // Clear - we're loading, not from user input
      }

      helperFormDataRef.current = helperData;
      setHelperFormData(helperData);
      setHasLoadedProgress(true);

      /*
       * A local mirror surviving to this point means a save never reached the server -
       * a dropped connection, an expired session, a closed tab. Offer it rather than
       * applying it: the mirror could be older than the server copy, and replacing good
       * data with stale data would be its own kind of loss.
       */
      const localBackup = readLocalDraft(studentIntakeId);
      if (localBackup) {
        const recoverable = countRecoverableChanges(localBackup.values, safeSavedSubmissionData);
        if (recoverable > 0) {
          setRecoverableDraft({ ...localBackup, recoverable });
        } else {
          clearLocalDraft(studentIntakeId); // nothing the server does not already have
        }
      }
    } catch (error) {
      console.error('Failed to load saved progress:', error);
      // Not critical, continue without saved progress
    } finally {
      // Ensure the rest of initialization is never blocked by progress-load failures.
      setHasLoadedProgress(true);
    }
  }, [studentIntakeId, autofillProfileData, householdInformation, allFormInstances, hasUserStartedFilling, studentIntake]);

  // Calculate current grade (memoized to avoid unnecessary re-renders)
  const currentGrade = useMemo(() => {
    // Prefer the helper form's prefixed grade (avoids matching an empty student_grade / other _grade first)
    const helperInst = allFormInstances.find((inst) =>
      (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
    );
    if (helperInst) {
      const hk = `${helperInst.id}_grade`;
      const hv = formValues[hk];
      if (isMeaningfulFormValue(hv)) {
        return hv;
      }
    }

    // Unprefixed grade (rare in combined forms)
    if (formValues['grade'] && (typeof formValues['grade'] === 'string' ? formValues['grade'].trim() !== '' : true)) {
      return formValues['grade'];
    }

    for (const key in formValues) {
      if (key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_grade$/i)) {
        const value = formValues[key];
        if (value && (typeof value === 'string' ? value.trim() !== '' : true)) {
          return value;
        }
      }
    }

    for (const key in formValues) {
      if (key.endsWith('_grade') && formValues[key] &&
          !key.includes('previous_school_grade') &&
          !key.includes('previous_enrollment_grade') &&
          !key.includes('retained_grade') &&
          !key.includes('sibling_grade')) {
        const value = formValues[key];
        if (value && (typeof value === 'string' ? value.trim() !== '' : true)) {
          return value;
        }
      }
    }

    // If formValues briefly loses grade (renderer/autofill race), keep filtering stable from helper aggregate
    const hg = helperFormData?.grade;
    if (
      hg !== null &&
      hg !== undefined &&
      hg !== '' &&
      hg !== 'Please select an option' &&
      !(typeof hg === 'string' && hg.trim() === '')
    ) {
      return hg;
    }

    return null;
  }, [formValues, helperFormData, allFormInstances]);

  // Calculate student grade from birthdate
  const calculateStudentGrade = useCallback((student) => {
    if (!student || !student.date_of_birth) return null;

    // Timezone-safe: treat DOB as a calendar date, never a UTC instant.
    const ymd = extractYMD(student.date_of_birth);
    if (!ymd) return null;
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12 to match ymd.month
    const birthMonth = ymd.month;

    let age = today.getFullYear() - ymd.year;

    // Adjust age if birthday hasn't occurred this year
    if (currentMonth < birthMonth || (currentMonth === birthMonth && today.getDate() < ymd.day)) {
      age--;
    }


    // Determine grade based on age (rough approximation)
    // Kindergarten: ~5 years old
    // Grade 1: ~6 years old
    // Grade 2: ~7 years old
    // etc.
    if (age < 5) return 'Preschool';
    if (age === 5) return 'Kindergarten';
    if (age >= 6 && age <= 17) return (age - 5).toString(); // Grade = age - 5
    return '12'; // Max grade
  }, []);

  // Filter form instances based on grade
  const filterFormInstancesByGrade = useCallback((instances, grade) => {
    if (!instances || instances.length === 0) return instances;

    return instances.filter(instance => {
      const formName = (instance.instance_name || instance.master_form_name || '').toLowerCase();

      // Exclude returning student form
      if (formName.includes('returning')) {
        return false;
      }

      // Exclude school lunch application
      if (formName.includes('school lunch') || formName.includes('lunch')) {
        return false;
      }

      // Chromebook Agreement: required for Kindergarten through 12, NOT for Preschool
      if (formName.includes('chromebook')) {
        if (!grade || grade === 'Preschool') {
          return false; // Hide for Preschool, and until a grade is chosen
        }
        // Show and mark as required for Kindergarten through grade 12
        instance.is_required = true; // Mark as required for this student
        return true;
      }

      // Early Dismissal Permissions: Required for K-6, NOT for 7-12
      if (formName.includes('early dismissal') || formName.includes('dismissal')) {
        if (!grade) {
          return true; // Show if grade not selected yet
        }
        const numericGrade = parseInt(grade);
        if (isNaN(numericGrade)) {
          // Handle non-numeric grades (Kindergarten, Preschool)
          if (grade === 'Kindergarten' || grade === 'Preschool') {
            instance.is_required = true; // Mark as required
            return true; // Show for K/Preschool
          }
          return true; // Default to showing if can't parse
        }

        // Hide for grades 7-12
        if (numericGrade >= 7 && numericGrade <= 12) {
          return false;
        }

        // Show and mark as required for grades K-6
        instance.is_required = true; // Mark as required
        return true;
      }

      // Include all other forms
      return true;
    });
  }, []);

  const fetchFormInstances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/form-instances/parent?enrollment_type=new');

      if (response.data && response.data.length > 0) {
        // Household Information is now the autofill source; exclude Helper Form instances.
        const nonHelperInstances = response.data.filter((instance) => !isHelperFormInstanceName(instance));

        // Store all usable instances
        setAllFormInstances(nonHelperInstances);

        // Get current grade from student intake data (form values not available yet during initial load)
        const studentGrade = calculateStudentGrade(studentIntake);
        const currentGrade = studentGrade;

        // Exclude helper form when there are completed submissions

        // Get filtered instances
        const finalInstances = filterFormInstancesByGrade(nonHelperInstances, currentGrade);

        if (finalInstances.length === 0) {
          setError('No new student forms found for your district');
          setLoading(false);
          return;
        }

        // Sort forms in the desired order for parents
        const desiredOrder = [
          'Emergency Health form',
          'Residency verification',
          'Acceptable use agreement',
          'New Student form',
          'Chromebook agreement',
          'Early Dismissal Permissions'
        ];

        const sortedInstances = finalInstances.sort((a, b) => {
          const aName = a.instance_name || a.master_form_name || '';
          const bName = b.instance_name || b.master_form_name || '';

          // Find positions in desired order
          let aIndex = desiredOrder.length;
          let bIndex = desiredOrder.length;

          for (let i = 0; i < desiredOrder.length; i++) {
            if (aName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
                desiredOrder[i].toLowerCase().includes(aName.toLowerCase())) {
              aIndex = i;
            }
            if (bName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
                desiredOrder[i].toLowerCase().includes(bName.toLowerCase())) {
              bIndex = i;
            }
          }

          return aIndex - bIndex;
        });

        // Store current scroll position before updating to prevent page jumping
        const scrollY = window.scrollY;
        setScrollPosition(scrollY);

        setFormInstances(sortedInstances);

        // Combine all form instances into one
        const combined = combineFormInstances(sortedInstances);
        if (combined) {
          setCombinedFormDefinition(combined);
          setFormInstanceMap(combined.formInstanceMap);
          const fieldDefinitionMap = buildFieldDefinitionMap(combined);

          // Initialize form values with all possible field keys from the combined form
          const initializedFormValues = {};
          Object.keys(combined.formInstanceMap).forEach(fieldKey => {
            initializedFormValues[fieldKey] = '';
          });

          // Pre-populate with student intake data if available
          if (studentIntake) {
            Object.keys(initializedFormValues).forEach(fieldKey => {
              const parts = fieldKey.split('_');
              if (parts.length >= 2) {
                const originalFieldName = parts.slice(1).join('_');
                // Populate common fields from student intake using canonical + alias field names.
                const rawIntakeValue = safeResolveStudentIntakeFieldValue(originalFieldName, studentIntake);
                const intakeValue = normalizeStudentAutofillValueForField({
                  originalFieldName,
                  intakeValue: rawIntakeValue,
                  fieldDefinition: fieldDefinitionMap[fieldKey],
                  studentIntake
                });
                if (!isAutofillValueEmpty(intakeValue)) {
                  initializedFormValues[fieldKey] = intakeValue;
                }
              }
            });
          }

          setFormValues(initializedFormValues);
          previousFormValuesRef.current = initializedFormValues;

          // Initialize autofill enabled state for forms that get autofilled
          const initialAutofillEnabled = {};
          sortedInstances.forEach(instance => {
            if (isFormAutofilled(instance)) {
              initialAutofillEnabled[instance.id] = true; // Default to enabled
            }
          });
          setAutofillEnabled(initialAutofillEnabled);
        } else {
          setError('Failed to combine form instances');
        }
      } else {
        setError('No active forms found for your district');
      }
    } catch (error) {
      console.error('Failed to fetch form instances:', error);
      setError('Failed to load forms. Please try again later.');
    } finally {
      setLoading(false);
      // Load saved progress after forms are loaded
      loadSavedProgress();
    }
  }, [studentIntake, filterFormInstancesByGrade]); // Re-enabled safe dependencies

  // Load saved progress when student intake data becomes available
  // Load saved progress when student intake data is available
  useEffect(() => {
    if (studentIntakeId && studentIntake && combinedFormDefinition) {
      loadSavedProgress();
    }
  }, [studentIntakeId, studentIntake, combinedFormDefinition]); // loadSavedProgress dependency removed to avoid init issues


  // Extract helper form data from completed submission

  const calculateProgress = useCallback(() => {
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return 0;
    }

    let totalFields = 0;
    let filledFields = 0;

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;
      
      section.fields.forEach(field => {
        if (field.admin_only) return;
        
        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          const currentValue = formValues[conditionalField];
          isVisible = Array.isArray(conditionalValue) 
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        totalFields++;
        const value = formValues[field.name];
        
        if (field.type === 'checkbox') {
          if (value && Array.isArray(value) && value.length > 0) {
            filledFields++;
          }
        } else if (value && (typeof value === 'string' && value.trim() !== '')) {
          filledFields++;
        }
      });
    });

    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  }, [combinedFormDefinition, formValues]);

  const saveProgress = useCallback(async (options = {}) => {
    const trigger = options.trigger === 'manual' ? 'manual' : 'auto';
    if (savingProgress) return;

    try {
      setSavingProgress(true);
      const progressPercentage = calculateProgress();

      await axios.post('/api/form-submissions/save-progress', {
        submission_data: sanitizeForJsonObject(formValues),
        progress_percentage: progressPercentage,
        form_instance_id: null, // Combined form has null form_instance_id
        student_intake_id: studentIntakeId, // Link to specific student
        form_type: 'new_student'
      });
      setLastSavedTime(new Date());
      // The server has it; the local mirror has done its job.
      clearLocalDraft(studentIntakeId);
      setRecoverableDraft(null);

      window.dispatchEvent(new CustomEvent('progressUpdated'));

      if (trigger === 'manual') {
        setSaveFeedbackModal({
          variant: 'success',
          title: 'Progress saved',
          message: 'Your form progress has been saved. You can return anytime to finish.'
        });
      } else if (!autoSaveNoticeShownRef.current) {
        autoSaveNoticeShownRef.current = true;
        setAutoSaveToast({
          variant: 'success',
          message: 'Progress saved. We keep saving as you go, so you can leave and come back.'
        });
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
      console.error('Error details:', error.response?.data || error.message);
      const msg =
        error.response?.data?.error || error.message || 'Could not save progress. Please try again.';
      if (trigger === 'manual') {
        setSaveFeedbackModal({
          variant: 'error',
          title: 'Save failed',
          message: msg
        });
      } else {
        setAutoSaveToast({ variant: 'error', message: 'Could not save progress automatically.' });
      }
    } finally {
      setSavingProgress(false);
    }
  }, [formValues, savingProgress, calculateProgress, studentIntakeId]);

  useEffect(() => {
    if (!studentIntakeId || !studentIntake) return;
    if (formInstancesInitializedForRef.current === studentIntakeId) return;
    formInstancesInitializedForRef.current = studentIntakeId;
    fetchFormInstances();
  }, [studentIntakeId, studentIntake]); // fetchFormInstances dependency intentionally omitted to avoid init loops

  // One-time backfill pass per student/form render; fills only blank student fields.
  useEffect(() => {
    if (!studentIntakeId || !studentIntake || !combinedFormDefinition) return;
    const formFieldKeyCount = Object.keys(formValues || {}).length;
    if (formFieldKeyCount === 0) return;
    const backfillSourceKey = `${studentIntakeId}|${formFieldKeyCount}`;
    if (studentBackfillAppliedForRef.current === backfillSourceKey) return;
    setFormValues((prev) => {
      const result = buildStudentIntakeBackfillResult(prev, studentIntake, combinedFormDefinition);
      const { nextValues, changed } = result;
      if (changed) {
        previousFormValuesRef.current = nextValues;
        studentBackfillAppliedForRef.current = backfillSourceKey;
      }
      return nextValues;
    });
  }, [studentIntakeId, studentIntake, combinedFormDefinition, formValues]);


  // Re-filter form instances when grade changes or allFormInstances changes
  useEffect(() => {
    if (allFormInstances.length === 0) return;

    // For dynamic filtering, exclude helper form when there are completed submissions
    const filteredInstances = filterFormInstancesByGrade(allFormInstances, currentGrade);


    // Sort forms in the desired order for parents
    const desiredOrder = [
      'Emergency Health form',
      'Residency verification',
      'Acceptable use agreement',
      'New Student form',
      'Chromebook agreement',
      'Early Dismissal Permissions'
    ];

    const sortedInstances = filteredInstances.sort((a, b) => {
      const aName = a.instance_name || a.master_form_name || '';
      const bName = b.instance_name || b.master_form_name || '';

      // Find positions in desired order
      let aIndex = desiredOrder.length;
      let bIndex = desiredOrder.length;

      for (let i = 0; i < desiredOrder.length; i++) {
        if (aName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
            desiredOrder[i].toLowerCase().includes(aName.toLowerCase())) {
          aIndex = i;
        }
        if (bName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
            desiredOrder[i].toLowerCase().includes(bName.toLowerCase())) {
          bIndex = i;
        }
      }

      return aIndex - bIndex;
    });

    // Only update if the filtered list changed
    const currentIds = formInstances.map(i => i.id).sort().join(',');
    const newIds = sortedInstances.map(i => i.id).sort().join(',');

    if (currentIds !== newIds) {
      // Store current scroll position before updating to prevent page jumping
      const scrollY = window.scrollY;
      setScrollPosition(scrollY);

      setFormInstances(sortedInstances);

      // Recombine form instances
      const combined = combineFormInstances(sortedInstances);
      if (combined) {
        setCombinedFormDefinition(combined);
        setFormInstanceMap(combined.formInstanceMap);

        // Initialize form values with all possible field keys from the combined form
        // Preserve existing values, especially grade fields which control visibility
        const initializedFormValues = { ...formValues };
        Object.keys(combined.formInstanceMap).forEach(fieldKey => {
          if (!(fieldKey in initializedFormValues)) {
            initializedFormValues[fieldKey] = '';
          }
        });
        // Don't overwrite grade fields - they're critical for form filtering
        // Find all grade fields (including prefixed ones like {instanceId}_grade)
        const gradeFields = Object.keys(formValues).filter(key => {
          const isUnprefixedGrade = key === 'grade';
          const isPrefixedGrade = key.endsWith('_grade') && 
            !key.includes('previous_school_grade') &&
            !key.includes('previous_enrollment_grade') && 
            !key.includes('retained_grade') &&
            !key.includes('sibling_grade');
          return isUnprefixedGrade || isPrefixedGrade;
        });
        
        // Preserve grade field values - check both old and new field keys
        gradeFields.forEach(gradeKey => {
          const oldValue = formValues[gradeKey];
          if (oldValue !== undefined && oldValue !== null && oldValue !== '') {
            initializedFormValues[gradeKey] = oldValue;
            Object.keys(combined.formInstanceMap).forEach(newFieldKey => {
              const newFieldName = newFieldKey.split('_').slice(1).join('_');
              const oldFieldName = gradeKey.split('_').slice(1).join('_');
              if (newFieldName === 'grade' && oldFieldName === 'grade' && newFieldKey !== gradeKey) {
                initializedFormValues[newFieldKey] = oldValue;
              }
            });
          }
        });

        // CRITICAL: Preserve ALL helper form fields (first name, last name, age, etc.) when grade changes
        // Use both formValues and previousFormValuesRef - ref may have latest from handleFormChange before state commits
        const helperInstance = sortedInstances.find(inst =>
          (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
        );
        if (helperInstance) {
          const sources = [formValues, previousFormValuesRef.current].filter(Boolean);
          sources.forEach(source => {
            Object.keys(source).forEach(key => {
              if (key.startsWith(`${helperInstance.id}_`)) {
                const val = source[key];
                if (val !== null && val !== undefined && val !== '' && val !== 'Please select an option' &&
                    !(Array.isArray(val) && val.length === 0)) {
                  initializedFormValues[key] = val;
                }
              }
            });
          });
        }

        setFormValues(initializedFormValues);
        previousFormValuesRef.current = initializedFormValues;

        // Initialize autofill enabled state for forms that get autofilled
        const initialAutofillEnabled = {};
        sortedInstances.forEach(instance => {
          if (isFormAutofilled(instance)) {
            initialAutofillEnabled[instance.id] = true; // Default to enabled
          }
        });
        setAutofillEnabled(initialAutofillEnabled);

        // Remove form data for forms that are no longer visible
        // But preserve grade fields since they affect form filtering
        const cleanedFormValues = {};
        const allKeys = new Set([...Object.keys(formValues), ...Object.keys(previousFormValuesRef.current || {})]);
        allKeys.forEach(key => {
          const belongsToVisible = filteredInstances.some(inst => key.startsWith(`${inst.id}_`));
          const isGradeField = key === 'grade' || key.endsWith('_grade') ||
            (key.includes('grade') && !key.includes('previous_school_grade') &&
            !key.includes('previous_enrollment_grade') && !key.includes('retained_grade') &&
            !key.includes('sibling_grade'));

          if (belongsToVisible || isGradeField) {
            const val = formValues[key] ?? previousFormValuesRef.current?.[key];
            if (val !== undefined) cleanedFormValues[key] = val;
          }
        });

        // Only update if values changed
        if (JSON.stringify(cleanedFormValues) !== JSON.stringify(formValues)) {
          setFormValues(cleanedFormValues);
          previousFormValuesRef.current = cleanedFormValues;
        }
      }
    }
  }, [allFormInstances, filterFormInstancesByGrade, currentGrade]);

  // Autofill fields from helper form data

  const applyHelperFormAutofill = useCallback((currentValues, helperData, allHelperData) => {
    if (!helperData || Object.keys(helperData).length === 0) {
      return currentValues;
    }

    const updatedValues = { ...currentValues };
    const profile = allHelperData || helperData;

    const setIfAllowedAndEmpty = (prefixedFieldName, nextValue) => {
      const parsed = parsePrefixedFieldName(prefixedFieldName);
      if (!parsed) return;
      const { instanceId } = parsed;
      if (autofillEnabled[instanceId] === false) return;
      if (manuallyClearedAutofillFields.has(prefixedFieldName)) return;
      if (isAutofillValueEmpty(nextValue)) return;
      if (!isAutofillValueEmpty(updatedValues[prefixedFieldName])) return;
      updatedValues[prefixedFieldName] = nextValue;
    };

    Object.keys(updatedValues).forEach((prefixedFieldName) => {
      const parsed = parsePrefixedFieldName(prefixedFieldName);
      if (!parsed) return;
      const { originalFieldName } = parsed;

      if (!isAutofillValueEmpty(profile[originalFieldName])) {
        setIfAllowedAndEmpty(prefixedFieldName, profile[originalFieldName]);
        return;
      }

      for (const [sourceField, aliases] of Object.entries(AUTOFILL_ALIAS_MAP)) {
        if (!aliases.includes(originalFieldName)) continue;
        setIfAllowedAndEmpty(prefixedFieldName, profile[sourceField]);
      }
    });

    const firstName = [
      profile.legal_name_first,
      profile.first_name,
      profile.student_first_name,
      profile.student_legal_first_name
    ].find((v) => !isAutofillValueEmpty(v));
    const lastName = [
      profile.legal_name_last,
      profile.last_name,
      profile.student_last_name,
      profile.student_legal_last_name
    ].find((v) => !isAutofillValueEmpty(v));
    const hasBothNames = !isAutofillValueEmpty(firstName) && !isAutofillValueEmpty(lastName);
    if (hasBothNames) {
      Object.keys(updatedValues).forEach((prefixedFieldName) => {
        const parsed = parsePrefixedFieldName(prefixedFieldName);
        if (!parsed || parsed.originalFieldName !== 'student_name') return;
        setIfAllowedAndEmpty(prefixedFieldName, `${String(firstName).trim()} ${String(lastName).trim()}`);
      });
    }

    const primaryParentName = [
      profile.primary_parent_first_name,
      profile.primary_parent_last_name
    ]
      .filter((v) => !isAutofillValueEmpty(v))
      .join(' ')
      .trim();
    if (primaryParentName) {
      Object.keys(updatedValues).forEach((prefixedFieldName) => {
        const parsed = parsePrefixedFieldName(prefixedFieldName);
        if (!parsed) return;
        if (parsed.originalFieldName === 'primary_parent_name' || parsed.originalFieldName === 'parent_name') {
          setIfAllowedAndEmpty(prefixedFieldName, primaryParentName);
        }
      });
    }

    const additionalParentName = [
      profile.additional_parent_first_name,
      profile.additional_parent_last_name
    ]
      .filter((v) => !isAutofillValueEmpty(v))
      .join(' ')
      .trim();
    if (additionalParentName) {
      Object.keys(updatedValues).forEach((prefixedFieldName) => {
        const parsed = parsePrefixedFieldName(prefixedFieldName);
        if (!parsed) return;
        if (parsed.originalFieldName === 'additional_parent_name') {
          setIfAllowedAndEmpty(prefixedFieldName, additionalParentName);
        }
      });
    }

    // Derive non-primary parent from mother/father profile data and use it for
    // alternate/additional parent fields when those fields are still empty.
    const motherCandidate = {
      firstName: profile.mother_guardian_first_name,
      lastName: profile.mother_guardian_last_name,
      cellPhone: profile.mother_cell_phone,
      homePhone: profile.mother_home_phone,
      email: profile.mother_email || profile.mother_guardian_email
    };
    const fatherCandidate = {
      firstName: profile.father_guardian_first_name,
      lastName: profile.father_guardian_last_name,
      cellPhone: profile.father_cell_phone,
      homePhone: profile.father_home_phone,
      email: profile.father_email || profile.father_guardian_email
    };
    const hasCandidateData = (candidate) =>
      !isAutofillValueEmpty(candidate.firstName) ||
      !isAutofillValueEmpty(candidate.lastName) ||
      !isAutofillValueEmpty(candidate.cellPhone) ||
      !isAutofillValueEmpty(candidate.homePhone) ||
      !isAutofillValueEmpty(candidate.email);
    const scoreCandidate = (candidate) =>
      Number(!isAutofillValueEmpty(candidate.firstName)) +
      Number(!isAutofillValueEmpty(candidate.lastName)) +
      Number(!isAutofillValueEmpty(candidate.cellPhone)) +
      Number(!isAutofillValueEmpty(candidate.homePhone)) +
      Number(!isAutofillValueEmpty(candidate.email));
    const sameAsPrimaryName = (candidate) => {
      const primaryFirst = String(profile.primary_parent_first_name || '').trim().toLowerCase();
      const primaryLast = String(profile.primary_parent_last_name || '').trim().toLowerCase();
      const candidateFirst = String(candidate.firstName || '').trim().toLowerCase();
      const candidateLast = String(candidate.lastName || '').trim().toLowerCase();
      if (!primaryFirst || !primaryLast || !candidateFirst || !candidateLast) return false;
      return primaryFirst === candidateFirst && primaryLast === candidateLast;
    };

    let nonPrimaryParent = null;
    if (profile.mother_same_as_primary && hasCandidateData(fatherCandidate)) {
      nonPrimaryParent = fatherCandidate;
    } else if (profile.father_same_as_primary && hasCandidateData(motherCandidate)) {
      nonPrimaryParent = motherCandidate;
    } else {
      const distinct = [motherCandidate, fatherCandidate].filter(
        (candidate) => hasCandidateData(candidate) && !sameAsPrimaryName(candidate)
      );
      if (distinct.length > 0) {
        nonPrimaryParent = distinct.sort((a, b) => scoreCandidate(b) - scoreCandidate(a))[0];
      }
    }

    if (nonPrimaryParent) {
      const nonPrimaryFullName = [nonPrimaryParent.firstName, nonPrimaryParent.lastName]
        .filter((v) => !isAutofillValueEmpty(v))
        .join(' ')
        .trim();
      const nonPrimaryPhone = !isAutofillValueEmpty(nonPrimaryParent.cellPhone)
        ? nonPrimaryParent.cellPhone
        : nonPrimaryParent.homePhone;

      Object.keys(updatedValues).forEach((prefixedFieldName) => {
        const parsed = parsePrefixedFieldName(prefixedFieldName);
        if (!parsed) return;
        const { originalFieldName } = parsed;
        if (
          originalFieldName === 'additional_parent_first_name' &&
          !isAutofillValueEmpty(nonPrimaryParent.firstName)
        ) {
          setIfAllowedAndEmpty(prefixedFieldName, nonPrimaryParent.firstName);
        }
        if (
          originalFieldName === 'additional_parent_last_name' &&
          !isAutofillValueEmpty(nonPrimaryParent.lastName)
        ) {
          setIfAllowedAndEmpty(prefixedFieldName, nonPrimaryParent.lastName);
        }
        if (
          originalFieldName === 'additional_parent_name' &&
          !isAutofillValueEmpty(nonPrimaryFullName)
        ) {
          setIfAllowedAndEmpty(prefixedFieldName, nonPrimaryFullName);
        }
        if (
          originalFieldName === 'additional_parent_phone' &&
          !isAutofillValueEmpty(nonPrimaryPhone)
        ) {
          setIfAllowedAndEmpty(prefixedFieldName, nonPrimaryPhone);
        }
        if (
          originalFieldName === 'additional_parent_email' &&
          !isAutofillValueEmpty(nonPrimaryParent.email)
        ) {
          setIfAllowedAndEmpty(prefixedFieldName, nonPrimaryParent.email);
        }
      });
    }

    if (profile.mother_same_as_primary) {
      const name = [profile.primary_parent_first_name, profile.primary_parent_last_name].filter(Boolean).join(' ');
      Object.keys(updatedValues).forEach((prefixedFieldName) => {
        const parsed = parsePrefixedFieldName(prefixedFieldName);
        if (!parsed) return;
        if (parsed.originalFieldName === 'mother_guardian_name') setIfAllowedAndEmpty(prefixedFieldName, name);
        if (parsed.originalFieldName === 'mother_home_phone') setIfAllowedAndEmpty(prefixedFieldName, profile.primary_parent_home_phone);
        if (parsed.originalFieldName === 'mother_cell_phone') setIfAllowedAndEmpty(prefixedFieldName, profile.primary_parent_cell_phone);
        if (parsed.originalFieldName === 'mother_email' || parsed.originalFieldName === 'mother_guardian_email') {
          setIfAllowedAndEmpty(prefixedFieldName, profile.primary_parent_email);
        }
      });
    }

    if (profile.father_same_as_primary) {
      const name = [profile.primary_parent_first_name, profile.primary_parent_last_name].filter(Boolean).join(' ');
      Object.keys(updatedValues).forEach((prefixedFieldName) => {
        const parsed = parsePrefixedFieldName(prefixedFieldName);
        if (!parsed) return;
        if (parsed.originalFieldName === 'father_guardian_name') setIfAllowedAndEmpty(prefixedFieldName, name);
        if (parsed.originalFieldName === 'father_home_phone') setIfAllowedAndEmpty(prefixedFieldName, profile.primary_parent_home_phone);
        if (parsed.originalFieldName === 'father_cell_phone') setIfAllowedAndEmpty(prefixedFieldName, profile.primary_parent_cell_phone);
        if (parsed.originalFieldName === 'father_email' || parsed.originalFieldName === 'father_guardian_email') {
          setIfAllowedAndEmpty(prefixedFieldName, profile.primary_parent_email);
        }
      });
    }

    if (!isAutofillValueEmpty(profile.primary_parent_first_name) && !isAutofillValueEmpty(profile.primary_parent_last_name)) {
      const earlyDismissalInstance = allFormInstances?.find((inst) =>
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('early') &&
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('dismissal')
      );
      if (earlyDismissalInstance) {
        setIfAllowedAndEmpty(
          `${earlyDismissalInstance.id}_authorized_person_last_name`,
          profile.primary_parent_last_name
        );
        setIfAllowedAndEmpty(
          `${earlyDismissalInstance.id}_authorized_person_first_name`,
          profile.primary_parent_first_name
        );
      }
    }

    return updatedValues;
  }, [allFormInstances, autofillEnabled, manuallyClearedAutofillFields]);

  const autofillSiblingRepeatGroups = useCallback((currentValues) => {
    if (!combinedFormDefinition?.sections || siblings.length === 0) {
      return currentValues;
    }

    let hasChanges = false;
    const updatedValues = { ...currentValues };

    combinedFormDefinition.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.type !== 'repeatable_group') return;

        const hasSiblingFields = field.fields?.some(subField =>
          subField.name.toLowerCase().includes('sibling')
        );

        if (!hasSiblingFields) return;

        const existingEntries = currentValues[field.name];
        if (Array.isArray(existingEntries) && existingEntries.length > 0) {
          return;
        }

        const entries = siblings.map((sibling) => {
          const entry = {};
          field.fields.forEach(subField => {
            if (subField.name === 'sibling_name') {
              const firstName = sibling.first_name || sibling.legal_name_first || '';
              const lastName = sibling.last_name || sibling.legal_name_last || '';
              entry.sibling_name = `${firstName} ${lastName}`.trim();
            }
            if (subField.name === 'sibling_grade') {
              entry.sibling_grade = sibling.grade || sibling.grade_level || '';
            }
            if (subField.name === 'sibling_building') {
              entry.sibling_building = sibling.school_name || sibling.school || '';
            }
          });
          return entry;
        }).filter(entry => entry.sibling_name);

        if (entries.length > 0) {
          updatedValues[field.name] = entries;
          hasChanges = true;
        }
      });
    });

    return hasChanges ? updatedValues : currentValues;
  }, [combinedFormDefinition, siblings]);

  const handleAddFromStudentList = useCallback(() => {
    if (!combinedFormDefinition || siblings.length === 0) {
      setSiblingAutofillMessage('No students available to add');
      return;
    }

    const updatedValues = { ...formValues };
    let addedCount = 0;

    combinedFormDefinition.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.type !== 'repeatable_group') return;
        if (!field.fields?.some(subField => subField.name.toLowerCase().includes('sibling'))) return;

        const fieldName = field.name;
        const existingEntries = Array.isArray(formValues[fieldName]) ? [...formValues[fieldName]] : [];
        const existingNames = new Set(
          existingEntries
            .map(entry => (entry?.sibling_name || '').trim().toLowerCase())
            .filter(Boolean)
        );

        const newEntries = [];
        siblings.forEach(sibling => {
          const firstName = sibling.first_name || sibling.legal_name_first || '';
          const lastName = sibling.last_name || sibling.legal_name_last || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (!fullName) return;
          if (existingNames.has(fullName.toLowerCase())) return;
          existingNames.add(fullName.toLowerCase());

          const entry = {};
          field.fields.forEach(subField => {
            if (subField.name === 'sibling_name') {
              entry.sibling_name = fullName;
            }
            if (subField.name === 'sibling_grade') {
              entry.sibling_grade = sibling.grade || sibling.grade_level || '';
            }
            if (subField.name === 'sibling_building') {
              entry.sibling_building = sibling.school_name || sibling.school || '';
            }
          });

          if (Object.keys(entry).length > 0) {
            newEntries.push(entry);
          }
        });

        if (newEntries.length > 0) {
          updatedValues[fieldName] = [...existingEntries, ...newEntries];
          addedCount += newEntries.length;
        }
      });
    });

    if (addedCount > 0) {
      setFormValues(updatedValues);
      setSiblingAutofillMessage(`Added ${addedCount} sibling${addedCount > 1 ? 's' : ''} from student list.`);
    } else {
      setSiblingAutofillMessage('No new students to add');
    }
  }, [combinedFormDefinition, siblings, formValues]);

  useEffect(() => {
    if (
      !combinedFormDefinition ||
      siblings.length === 0 ||
      hasLoadedProgress ||
      hasUserStartedFilling ||
      siblingsAutofillRef.current === studentIntakeId
    ) {
      return;
    }

    const updatedValues = autofillSiblingRepeatGroups(formValues);
    if (updatedValues !== formValues) {
      setFormValues(updatedValues);
    }

    siblingsAutofillRef.current = studentIntakeId;
  }, [
    combinedFormDefinition,
    siblings,
    hasLoadedProgress,
    hasUserStartedFilling,
    studentIntakeId,
    formValues,
    autofillSiblingRepeatGroups
  ]);

  // Apply autofill when helper form data changes OR when forms first load
  useEffect(() => {

    // For in-progress forms (saved but not submitted), allow autofill to update with new helper data
    // Only skip autofill if this is an already submitted form being edited (which doesn't happen in NewStudentForm)

    // Only run autofill if we have helper data AND forms are loaded
    // For in-progress forms, allow autofill even if progress was loaded
    // Don't run on every formValues change (user input) - only on helper data changes or initial load
    if (Object.keys(helperFormData).length > 0 && combinedFormDefinition) {
      // CRITICAL: Use latestFormValuesRef when available - captures user changes before state commits
      // This fixes race where useEffect runs before setFormValues has committed
      const valuesToUse = (latestFormValuesRef.current && Object.keys(latestFormValuesRef.current).length > 0)
        ? latestFormValuesRef.current
        : formValues;

      // CRITICAL: Preserve ALL helper form field values from current form state before autofill
      // The helper form is the SOURCE - never let autofill overwrite it (fixes grade clearing when selecting gender/ethnicity)
      const helperFormInstance = allFormInstances.find(inst => 
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
      );
      
      const preservedHelperFields = {};
      if (helperFormInstance) {
        // Preserve ALL non-empty helper form fields from current form state
        Object.keys(valuesToUse).forEach(key => {
          if (key.startsWith(`${helperFormInstance.id}_`)) {
            const currentValue = valuesToUse[key];
            const isEmpty = currentValue === null || currentValue === undefined || currentValue === '' ||
              (typeof currentValue === 'string' && currentValue === 'Please select an option') ||
              (Array.isArray(currentValue) && currentValue.length === 0);
            if (!isEmpty) {
              preservedHelperFields[key] = currentValue;
            }
          }
        });
      }
      
      const autofilledValues = applyHelperFormAutofill(valuesToUse, helperFormData, helperFormData);
      
      // CRITICAL: Restore ALL helper form field values - never let autofill overwrite the source
      Object.keys(preservedHelperFields).forEach(fieldKey => {
        autofilledValues[fieldKey] = preservedHelperFields[fieldKey];
      });

      // helperFormData is authoritative for the helper form. preservedHelperFields is built from
      // valuesToUse/ref, which can still hold an empty/placeholder grade when ethnicity (etc.) updates
      // helperFormData — restoring those would clear grade after the autofill merge.
      if (helperFormInstance) {
        const isHelperValEmpty = (v) =>
          v === null || v === undefined || v === '' || v === 'Please select an option' ||
          (Array.isArray(v) && v.length === 0);
        Object.keys(helperFormData).forEach((logicalName) => {
          const v = helperFormData[logicalName];
          if (isHelperValEmpty(v)) return;
          autofilledValues[`${helperFormInstance.id}_${logicalName}`] = v;
        });
      }

      if (JSON.stringify(autofilledValues) !== JSON.stringify(valuesToUse)) {
        // Never replace entire state with autofill output — it can omit keys or clear helper fields
        // (e.g. ethnicity checkbox updates helperFormData while valuesToUse lacks grade).
        setFormValues((prev) => {
          let next = { ...prev, ...autofilledValues };
          if (helperFormInstance) {
            const prefix = `${helperFormInstance.id}_`;
            Object.keys(prev).forEach((k) => {
              if (!k.startsWith(prefix)) return;
              if (isMeaningfulFormValue(prev[k]) && !isMeaningfulFormValue(next[k])) {
                next[k] = prev[k];
              }
            });
          }
          return next;
        });
      }
      // Clear the ref after use so we don't use stale data on subsequent runs
      latestFormValuesRef.current = null;
    } else {
    }
  // NOTE: formValues intentionally excluded from deps - including it causes a loop:
  // setFormValues triggers re-run, which can overwrite user's grade, causing vacillation
  }, [helperFormData, applyHelperFormAutofill, combinedFormDefinition, hasLoadedProgress, allFormInstances]);

  // Auto-populate Authorized Pick-Up Persons with parent and emergency contact names
  useEffect(() => {
    if (!combinedFormDefinition || !formInstances.length || Object.keys(helperFormData).length === 0) {
      return;
    }

    // Find Early Dismissal Permissions form instance
    const earlyDismissalInstance = formInstances.find(inst => 
      (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('early dismissal')
    );

    if (!earlyDismissalInstance) {
      return;
    }

    const instanceId = earlyDismissalInstance.id;
    // Authorized Pick-Up Persons is section index 4 (0-indexed: Form Info=0, Student Info=1, Parent Contact=2, Dismissal Permissions=3, Authorized Pick-Up=4)
    const authorizedPersonsFieldName = `${instanceId}_repeating_group_4`;

    // Get parent and emergency contact names from helper form data
    const personsToAdd = [];
    
    // Primary parent
    if (helperFormData.primary_parent_first_name && helperFormData.primary_parent_last_name) {
      const fullName = `${helperFormData.primary_parent_first_name} ${helperFormData.primary_parent_last_name}`;
      if (!deletedAuthorizedPersons.has(fullName)) {
        personsToAdd.push({
          authorized_person_first_name: helperFormData.primary_parent_first_name,
          authorized_person_last_name: helperFormData.primary_parent_last_name
        });
      }
    }

    // Alternate parent
    if (helperFormData.alternate_parent_first_name && helperFormData.alternate_parent_last_name) {
      const fullName = `${helperFormData.alternate_parent_first_name} ${helperFormData.alternate_parent_last_name}`;
      if (!deletedAuthorizedPersons.has(fullName)) {
        personsToAdd.push({
          authorized_person_first_name: helperFormData.alternate_parent_first_name,
          authorized_person_last_name: helperFormData.alternate_parent_last_name
        });
      }
    }

    // Emergency contacts (check for multiple emergency contacts)
    const emergencyContactKeys = Object.keys(helperFormData).filter(key => 
      key.startsWith('emergency_contact') && (key.includes('first_name') || key.includes('last_name'))
    );

    // Group emergency contacts by index
    const emergencyContactIndices = new Set();
    emergencyContactKeys.forEach(key => {
      const match = key.match(/emergency_contact_(\d+)_(first_name|last_name)/);
      if (match) {
        emergencyContactIndices.add(match[1]);
      } else if (key === 'emergency_contact_first_name' || key === 'emergency_contact_last_name') {
        emergencyContactIndices.add('0'); // Default index for non-indexed fields
      }
    });

    emergencyContactIndices.forEach(index => {
      const firstNameKey = index === '0' ? 'emergency_contact_first_name' : `emergency_contact_${index}_first_name`;
      const lastNameKey = index === '0' ? 'emergency_contact_last_name' : `emergency_contact_${index}_last_name`;
      
      const firstName = helperFormData[firstNameKey];
      const lastName = helperFormData[lastNameKey];
      
      if (firstName && lastName) {
        const fullName = `${firstName} ${lastName}`;
        if (!deletedAuthorizedPersons.has(fullName)) {
          personsToAdd.push({
            authorized_person_first_name: firstName,
            authorized_person_last_name: lastName
          });
        }
      }
    });

    if (personsToAdd.length === 0) {
      return;
    }

    // Get current authorized persons from form values (repeatable group is stored as array)
    const currentAuthorizedPersons = Array.isArray(formValues[authorizedPersonsFieldName]) 
      ? formValues[authorizedPersonsFieldName] 
      : [];
    const existingNames = new Set(
      currentAuthorizedPersons.map(person => 
        `${person.authorized_person_first_name || ''} ${person.authorized_person_last_name || ''}`.trim()
      )
    );

    // Only add persons that aren't already in the list
    const newPersons = personsToAdd.filter(person => {
      const fullName = `${person.authorized_person_first_name} ${person.authorized_person_last_name}`;
      return !existingNames.has(fullName);
    });

    if (newPersons.length > 0) {
      setFormValues(prev => ({
        ...prev,
        [authorizedPersonsFieldName]: [...currentAuthorizedPersons, ...newPersons]
      }));
    }
  }, [helperFormData, combinedFormDefinition, formInstances, deletedAuthorizedPersons, formValues]);

  // Run autofill once after progress is loaded and forms are ready
  // TEMPORARILY DISABLED for editing existing submissions
  useEffect(() => {
    if (hasLoadedProgress && combinedFormDefinition && !hasRunInitialAutofill) {
      // Temporarily disable autofill for existing submissions
      // if (Object.keys(helperFormData).length > 0) {
      //   const autofilledValues = applyHelperFormAutofill(formValues, helperFormData, helperFormData);
      //   if (JSON.stringify(autofilledValues) !== JSON.stringify(formValues)) {
      //     setFormValues(autofilledValues);
      //   }
      // }
      setHasRunInitialAutofill(true);
    }
  }, [hasLoadedProgress, combinedFormDefinition, hasRunInitialAutofill]); // Removed helperFormData, formValues, applyHelperFormAutofill dependencies

  // Restore scroll position after form updates to prevent page jumping
  useEffect(() => {
    if (scrollPosition > 0) {
      // Use setTimeout to ensure the DOM has updated before scrolling
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
        setScrollPosition(0); // Reset after restoring
      }, 0);
    }
  }, [combinedFormDefinition, scrollPosition]);

  // Auto-save progress every 30 seconds (disabled for editing completed submissions)
  useEffect(() => {
    if (Object.keys(formValues).length === 0) return;

    const autoSaveTimer = setTimeout(() => {
      saveProgress({ trigger: 'auto' });
    }, 30000); // 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formValues, saveProgress]);

  /**
   * Mirror locally, well inside the 30-second autosave window. This is what makes a failed
   * save survivable: the mirror is cleared the moment a save succeeds, so anything sitting
   * here at load time is work the server never received.
   */
  useEffect(() => {
    if (!studentIntakeId || Object.keys(formValues).length === 0) return undefined;
    const t = setTimeout(() => saveLocalDraft(studentIntakeId, formValues), 1500);
    return () => clearTimeout(t);
  }, [formValues, studentIntakeId]);

  /**
   * Warn before leaving with work the server has not acknowledged. Browsers show their own
   * wording; the point is that the tab does not close silently on unsaved answers.
   */
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!studentIntakeId) return undefined;
      if (!readLocalDraft(studentIntakeId)) return undefined; // nothing unsaved
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [studentIntakeId]);


  const validateForm = () => {
    const errors = {};
    
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return errors;
    }

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;
      
      section.fields.forEach(field => {
        if (field.admin_only) return;
        
        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          // The conditional field should already be prefixed by combineFormInstances
          // But check both prefixed and unprefixed versions for safety
          let currentValue = formValues[conditionalField];
          if (currentValue === undefined && field.form_instance_id) {
            // Try with instance ID prefix if not found
            const prefixedConditionalField = `${field.form_instance_id}_${conditionalField}`;
            currentValue = formValues[prefixedConditionalField];
          }
          isVisible = Array.isArray(conditionalValue)
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        // Check required
        const isRequired = field.required || 
          (field.conditional_required && isVisible);
        
        if (isRequired) {
          const value = formValues[field.name];
          
          // Handle different field types
          if (field.type === 'checkbox') {
            if (!value || !Array.isArray(value) || value.length === 0) {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'signature') {
            // Signature fields can be string (old format) or object with image property
            if (!value || 
                (typeof value === 'string' && value.trim() === '') ||
                (typeof value === 'object' && !value.image)) {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'date') {
            if (!value || value.trim() === '') {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'map') {
            if (!mapFieldHasPlacedMarker(value)) {
              errors[field.name] =
                `${field.label}: place a marker on the map (use Find on Map or click the map).`;
            }
          } else if (!value || (typeof value === 'string' && value.trim() === '')) {
            errors[field.name] = `${field.label} is required`;
          }
        }

        // Phone format validation: show warning when value exists but invalid (doesn't block editing)
        if (field.type === 'phone') {
          const value = formValues[field.name];
          if (value && typeof value === 'string') {
            const digits = value.replace(/\D/g, '');
            if (digits.length > 0 && digits.length !== 10) {
              errors[field.name] = 'Please enter a valid phone number';
            }
          }
        }
      });
    });

    return errors;
  };

  // Check if form is complete (all required fields filled)
  const isFormComplete = useCallback(() => {
    const errors = validateForm();
    return Object.keys(errors).length === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, combinedFormDefinition]);

  // The questionnaire's three questions are required by the state, so they belong in
  // the same missing-fields bar as everything else rather than in a separate message.
  // Labels come from the questionnaire component so this bar always names exactly the
  // fields that component requires, including any added later.
  const MIGRANT_QUESTION_LABELS = MIGRANT_FIELD_LABELS;
  const migrantMissingKeys = migrantAlreadyOnFile ? [] : missingMigrantAnswers(migrantAnswers);

  // Get form completion status
  const getFormCompletionStatus = () => {
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return { isComplete: false, missingFields: [], missingFieldDetails: [] };
    }

    const missingFields = [];
    const missingFieldDetails = [];
    // Every required field the parent can currently see, filled or not.
    let requiredCount = 0;
    
    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      // Check section-level conditionals (same logic as FormRenderer)
      if (section.conditional) {
        const conditionalField = section.conditional.field;
        const conditionalValue = section.conditional.value;

        // The conditional field should already be prefixed by combineFormInstances
        // But check both prefixed and unprefixed versions for safety
        let currentValue = formValues[conditionalField];
        if (currentValue === undefined && section.form_instance_id) {
          // Try with instance ID prefix if not found
          const prefixedConditionalField = `${section.form_instance_id}_${conditionalField}`;
          currentValue = formValues[prefixedConditionalField];
        }

        // If conditional value is a string, convert to array for includes check
        const valuesToCheck = Array.isArray(conditionalValue) ? conditionalValue : [conditionalValue];

        // Skip section if conditional is not met
        if (!valuesToCheck.includes(currentValue)) {
          return; // Skip entire section
        }
      }

      const sectionMissingFields = [];
      
      section.fields.forEach(field => {
        if (field.admin_only) return;
        
        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          // The conditional field should already be prefixed by combineFormInstances
          // But check both prefixed and unprefixed versions for safety
          let currentValue = formValues[conditionalField];
          if (currentValue === undefined && field.form_instance_id) {
            // Try with instance ID prefix if not found
            const prefixedConditionalField = `${field.form_instance_id}_${conditionalField}`;
            currentValue = formValues[prefixedConditionalField];
          }
          isVisible = Array.isArray(conditionalValue)
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        // Check required
        const isRequired = field.required || 
          (field.conditional_required && isVisible);
        
        if (isRequired) {
          // Counted whether filled or not, so progress can be shown as a fraction of
          // what this parent actually has to answer. Only visible fields reach here, so
          // a conditional section they never opened is not held against them.
          requiredCount += 1;

          const value = formValues[field.name];
          let isEmpty = false;

          if (field.type === 'checkbox') {
            isEmpty = !value || !Array.isArray(value) || value.length === 0;
          } else if (field.type === 'signature') {
            isEmpty = !value ||
              (typeof value === 'string' && value.trim() === '') ||
              (typeof value === 'object' && !value.image);
          } else if (field.type === 'date') {
            isEmpty = !value || value.trim() === '';
          } else if (field.type === 'map') {
            isEmpty = !mapFieldHasPlacedMarker(value);
          } else {
            isEmpty = !value || (typeof value === 'string' && value.trim() === '');
          }

          if (isEmpty) {
            missingFields.push(field.label);
            sectionMissingFields.push({ label: field.label, name: field.name, type: field.type });
          }
        }
      });

      // Group by section for display
      if (sectionMissingFields.length > 0) {
        const baseSectionId = section.section_name ? section.section_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : 'unknown-section';
        // Make section ID unique by including form instance ID to avoid conflicts between forms
        const sectionId = section.form_instance_id ? `${baseSectionId}-${section.form_instance_id}` : baseSectionId;
        missingFieldDetails.push({
          sectionName: section.section_name,
          fields: sectionMissingFields,
          sectionId: sectionId
        });
      }
    });

    if (migrantMissingKeys.length > 0) {
      const fields = migrantMissingKeys.map((k) => ({
        name: `migrant_${k}`,
        label: MIGRANT_QUESTION_LABELS[k],
        type: 'select'
      }));
      fields.forEach((f) => missingFields.push(f.label));
      missingFieldDetails.push({
        sectionName: 'Migrant Education Questionnaire',
        fields,
        sectionId: 'migrant-questionnaire-block'
      });
    }

    // The questionnaire's required questions count too, so the bar reflects the whole
    // job rather than jumping when the parent reaches that block.
    const migrantRequired = migrantAlreadyOnFile ? 0 : migrantTotalRequired(migrantAnswers);
    const totalRequired = requiredCount + migrantRequired;
    const answered = Math.max(0, totalRequired - missingFields.length);

    return {
      isComplete: missingFields.length === 0,
      missingFields,
      missingFieldDetails,
      totalRequired,
      answered,
      // Guard the divide: a definition with no required fields would otherwise be NaN%.
      percent: totalRequired > 0 ? Math.round((answered / totalRequired) * 100) : 0
    };
  };

  const scrollToMissingField = useCallback((field, section) => {
    if (field.type === 'map') {
      const wrap = document.getElementById(`field-${field.name}`);
      const btn = document.getElementById(`map-find-on-map-${field.name}`);
      if (wrap) {
        wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        wrap.style.boxShadow = '0 0 0 3px #007bff';
        setTimeout(() => {
          wrap.style.boxShadow = '';
        }, 2000);
      }
      if (btn) {
        setTimeout(() => {
          btn.focus();
          btn.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 250);
      }
      return;
    }
    let fieldElement = document.getElementById(`field-${field.name}`);
    if (!fieldElement) {
      fieldElement = document.querySelector(`[name="${field.name}"]`);
    }
    if (fieldElement) {
      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fieldElement.style.boxShadow = '0 0 0 3px #007bff';
      setTimeout(() => {
        fieldElement.style.boxShadow = '';
      }, 2000);
    } else {
      const element = document.getElementById(section.sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  const handleFormChange = (newValues) => {
    // Mark that user has started filling out the form
    if (!hasUserStartedFilling) {
      setHasUserStartedFilling(true);
    }

    // Detect which field(s) changed by comparing with previous values
    const previousValues = previousFormValuesRef.current;
    const changedFields = [];
    
    // Find all changed fields
    Object.keys(newValues).forEach(fieldName => {
      const oldValue = previousValues[fieldName];
      const newValue = newValues[fieldName];
      
      // Compare values (handle arrays for checkboxes — copy before sort to avoid mutating ref/state)
      if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        const sortedOld = [...oldValue].sort();
        const sortedNew = [...newValue].sort();
        if (JSON.stringify(sortedOld) !== JSON.stringify(sortedNew)) {
          changedFields.push(fieldName);
        }
      } else if (oldValue !== newValue) {
        changedFields.push(fieldName);
      }
    });
    
    // Also check for removed fields
    Object.keys(previousValues).forEach(fieldName => {
      if (!(fieldName in newValues)) {
        changedFields.push(fieldName);
      }
    });

    // If user explicitly cleared a previously populated field, prevent autofill from
    // immediately restoring stale values (fixes "undeletable county"/similar behavior).
    if (changedFields.length > 0) {
      setManuallyClearedAutofillFields(prev => {
        const next = new Set(prev);
        changedFields.forEach((fieldName) => {
          const oldValue = previousValues[fieldName];
          const newValue = newValues[fieldName];
          const oldWasMeaningful = isMeaningfulFormValue(oldValue);
          const newIsEmpty = !isMeaningfulFormValue(newValue);
          if (oldWasMeaningful && newIsEmpty) {
            next.add(fieldName);
          } else if (!newIsEmpty) {
            next.delete(fieldName);
          }
        });
        return next;
      });
    }
    
    // Merge with previous values to avoid losing fields due to FormRenderer race
    // (e.g. grade can be lost when user quickly selects gender before re-render completes)
    let syncedValues = { ...previousFormValuesRef.current, ...newValues };

    // CRITICAL: Preserve all helper form fields - never let them be overwritten with empty
    const helperFormInstance = allFormInstances.find(inst =>
      (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
    );
    if (helperFormInstance) {
      [previousFormValuesRef.current, formValues].forEach(source => {
        Object.keys(source || {}).forEach(key => {
          if (key.startsWith(`${helperFormInstance.id}_`) && isMeaningfulFormValue(source[key])) {
            if (!isMeaningfulFormValue(syncedValues[key])) {
              syncedValues = { ...syncedValues, [key]: source[key] };
            }
          }
        });
      });
    }
    if (formInstances.length > 0 && Object.keys(formInstanceMap).length > 0) {
      changedFields.forEach(changedFieldName => {
        const newValue = newValues[changedFieldName];
        const syncUpdates = syncSharedFields(
          changedFieldName,
          newValue,
          syncedValues,
          formInstanceMap,
          formInstances
        );
        
        // Merge sync updates into syncedValues
        syncedValues = {
          ...syncedValues,
          ...syncUpdates
        };
      });
    }
    
    // Track helper form data for autofill - only update changed fields, never rebuild from syncedValues
    // (rebuilding caused all helper form fields to be deleted when moving between fields)
    // Merge helper updates into the latest state (functional update) so rapid field changes
    // (e.g. grade then ethnicity) never drop earlier helper keys from a stale closure.
    const helperFieldPatches = {};

    // Track deleted authorized persons (repeatable group field name pattern)
    changedFields.forEach(changedFieldName => {
      if (changedFieldName.includes('repeating_group_4')) {
        const oldValue = previousValues[changedFieldName];
        const newValue = syncedValues[changedFieldName];
        
        if (Array.isArray(oldValue) && Array.isArray(newValue) && newValue.length < oldValue.length) {
          // Someone was deleted - track their name
          const oldNames = new Set(
            oldValue.map(person => 
              `${person.authorized_person_first_name || ''} ${person.authorized_person_last_name || ''}`.trim()
            )
          );
          const newNames = new Set(
            newValue.map(person => 
              `${person.authorized_person_first_name || ''} ${person.authorized_person_last_name || ''}`.trim()
            )
          );
          
          // Find deleted names
          oldNames.forEach(name => {
            if (!newNames.has(name) && name) {
              setDeletedAuthorizedPersons(prev => new Set([...prev, name]));
            }
          });
        }
      }
    });

    // Check if any changed fields belong to the helper form
    changedFields.forEach(changedFieldName => {
      // Extract the original field name from the prefixed field name
      const parts = changedFieldName.split('_');
      if (parts.length >= 2) {
        const instanceId = parts[0];
        const originalFieldName = parts.slice(1).join('_');

        // Find the form instance to check if it's the helper form
        const instance = allFormInstances.find(inst => inst.id === instanceId);
        if (instance && (instance.instance_name || instance.master_form_name || '').toLowerCase().includes('helper')) {
          const newValue = syncedValues[changedFieldName];
          
          // CRITICAL: Track that this helper form field was manually changed by the user
          setManuallyChangedHelperFields(prev => new Set([...prev, originalFieldName]));
          
          // CRITICAL: Never update student_age in helperData from synced values
          if (originalFieldName !== 'student_age') {
            if (newValue !== null && newValue !== undefined && newValue !== '' && newValue !== 'Please select an option') {
              helperFieldPatches[originalFieldName] = newValue;
            }
            // NEVER delete helper form fields when we get empty - preserves first/last name, age when
            // selecting grade (FormRenderer can send stale/partial data that would incorrectly clear them)
          } else {
            // For student_age field, preserve the existing helperData value - don't update it
          }
        }
      }
    });

    // Merge patches with ref synchronously so grade (etc.) is never missing when only ethnicity updates
    const mergedHelperSnapshot = Object.keys(helperFieldPatches).length > 0
      ? { ...helperFormDataRef.current, ...helperFieldPatches }
      : helperFormDataRef.current;

    if (helperFormInstance) {
      Object.keys(mergedHelperSnapshot).forEach((logical) => {
        if (logical === 'student_age') return;
        const prefixedKey = `${helperFormInstance.id}_${logical}`;
        const v = mergedHelperSnapshot[logical];
        if (isMeaningfulFormValue(v) && !isMeaningfulFormValue(syncedValues[prefixedKey])) {
          syncedValues = { ...syncedValues, [prefixedKey]: v };
        }
      });
    }

    if (Object.keys(helperFieldPatches).length > 0) {
      helperFormDataRef.current = mergedHelperSnapshot;
      setHelperFormData(mergedHelperSnapshot);
    }

    // CRITICAL: Store latest values in ref BEFORE setState - ensures autofill useEffect
    // sees user's changes even if it runs before state commits
    latestFormValuesRef.current = syncedValues;

    // Preserve grade — prefer helper form's grade field (Object.keys order can hit another empty *_grade first)
    let gradeFieldKey = null;
    let preservedGradeValue = null;
    if (helperFormInstance) {
      const hgk = `${helperFormInstance.id}_grade`;
      if (isMeaningfulFormValue(syncedValues[hgk])) {
        gradeFieldKey = hgk;
        preservedGradeValue = syncedValues[hgk];
      }
    }
    if (!gradeFieldKey) {
      gradeFieldKey = Object.keys(syncedValues).find((key) => {
        const isUnprefixedGrade = key === 'grade';
        const isPrefixedGrade = key.endsWith('_grade') &&
          !key.includes('previous_school_grade') &&
          !key.includes('previous_enrollment_grade') &&
          !key.includes('retained_grade') &&
          !key.includes('sibling_grade');
        return isUnprefixedGrade || isPrefixedGrade;
      });
      preservedGradeValue = gradeFieldKey ? syncedValues[gradeFieldKey] : null;
    }

    // Check if conditional field changed to "Yes" - trigger autofill
    let shouldTriggerAutofill = false;
    changedFields.forEach(changedFieldName => {
      const parts = changedFieldName.split('_');
      if (parts.length >= 2) {
        const originalFieldName = parts.slice(1).join('_');
        if (originalFieldName === 'has_records_from_previous_school') {
          const newValue = syncedValues[changedFieldName];
          const oldValue = previousFormValuesRef.current[changedFieldName];
          if (newValue === 'Yes' && oldValue !== 'Yes') {
            shouldTriggerAutofill = true;
          }
        }
      }
    });

    // Ensure grade field is preserved in synced values
    if (gradeFieldKey && isMeaningfulFormValue(preservedGradeValue)) {
      syncedValues[gradeFieldKey] = preservedGradeValue;
    }

    // Update form values with synced values
    setFormValues(syncedValues);

    // Trigger autofill if conditional field changed to "Yes"
    if (shouldTriggerAutofill && Object.keys(helperFormData).length > 0 && combinedFormDefinition) {
      const autofilledValues = applyHelperFormAutofill(syncedValues, helperFormData, helperFormData);
      // Preserve grade field in autofilled values too
      if (gradeFieldKey && isMeaningfulFormValue(preservedGradeValue)) {
        autofilledValues[gradeFieldKey] = preservedGradeValue;
      }
      
      // CRITICAL: Preserve ALL manually changed helper form fields - never let autofill overwrite them
      const helperFormInstanceForConditional = allFormInstances.find(inst =>
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
      );
      if (helperFormInstanceForConditional) {
        manuallyChangedHelperFields.forEach(fieldName => {
          const helperFormFieldKey = `${helperFormInstanceForConditional.id}_${fieldName}`;
          const currentValue = syncedValues[helperFormFieldKey];
          if (isMeaningfulFormValue(currentValue)) {
            autofilledValues[helperFormFieldKey] = currentValue;
          }
        });
      }
      
      if (JSON.stringify(autofilledValues) !== JSON.stringify(syncedValues)) {
        setFormValues((prev) => {
          let next = { ...prev, ...autofilledValues };
          if (helperFormInstanceForConditional) {
            const prefix = `${helperFormInstanceForConditional.id}_`;
            Object.keys(prev).forEach((k) => {
              if (!k.startsWith(prefix)) return;
              if (isMeaningfulFormValue(prev[k]) && !isMeaningfulFormValue(next[k])) {
                next[k] = prev[k];
              }
            });
          }
          return next;
        });
      }
    }

    // Update ref for next comparison
    previousFormValuesRef.current = syncedValues;
    
    // Clear validation errors when user makes changes
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
  };

  const handleDeleteProgress = async () => {
    try {
      // First get the progress to find its ID
      const progressResponse = await axios.get(`/api/form-submissions/progress?student_intake_id=${studentIntakeId}&form_type=new_student`);
      const progressRows = Array.isArray(progressResponse.data) ? progressResponse.data : [];
      const combinedFormProgress = progressRows.find(progress =>
        progress.form_instance_id === null && !progress.is_complete
      );


      if (combinedFormProgress) {
        // Delete the specific progress by ID
        await axios.delete(`/api/form-submissions/progress/${combinedFormProgress.id}`);
      } else {
      }

      setFormValues({});

      // Clear the submission flag since user is restarting
      if (studentIntakeId) {
        sessionStorage.removeItem(`student_submitted_${studentIntakeId}`);
      }

      // Notify parent dashboard about progress update (progress deleted)
      window.dispatchEvent(new CustomEvent('progressUpdated'));
      setHasLoadedProgress(false);
      setHasUserStartedFilling(false);
      setShowDeleteConfirm(false);
      setSuccess(false);
      setError('');
      // Navigate away from the form
      navigate('/parent/students');
    } catch (error) {
      console.error('Failed to delete progress:', error);
      setError('Failed to delete form progress');
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const completionStatus = getFormCompletionStatus();
      const errorMessage = completionStatus.missingFields.length > 0
        ? `Please complete all required fields. Missing: ${completionStatus.missingFields.slice(0, 5).join(', ')}${completionStatus.missingFields.length > 5 ? '...' : ''}`
        : 'Please fill in all required fields';
      setError(errorMessage);
      
      // Scroll to first error (map fields have no input[name=...])
      const firstErrorField = Object.keys(errors)[0];
      const mapBtn = document.getElementById(`map-find-on-map-${firstErrorField}`);
      const fieldWrap = document.getElementById(`field-${firstErrorField}`);
      if (mapBtn || fieldWrap) {
        if (fieldWrap) fieldWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (mapBtn) setTimeout(() => mapBtn.focus(), 300);
      } else {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }

      return;
    }

    if (combinedFormDefinition && !confirmUngeocodedMapAddressIfNeeded(combinedFormDefinition, formValues)) {
      return;
    }

    // The state requires all three questionnaire answers, so block here rather than
    // submitting enrollment and leaving the questionnaire half-done.
    setMigrantError('');
    if (!migrantAlreadyOnFile) {
      const missing = missingMigrantAnswers(migrantAnswers);
      if (missing.length > 0) {
        setMigrantError('Please answer all three questions in the Migrant Education Parent Questionnaire below.');
        setError('Please answer all three questions in the Migrant Education Parent Questionnaire.');
        const block = document.getElementById('migrant-questionnaire-block');
        if (block) block.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    setLoading(true);

    try {
      // Extract student info from form data
      const studentInfo = extractStudentInfo(formValues, formInstanceMap, formInstances);
      
      if (!studentInfo || !studentInfo.first_name || !studentInfo.last_name) {
        setError('Student name is required. Please fill in the student information section.');
        setLoading(false);
        return;
      }

      // Split form data by instance
      const splitData = splitFormDataByInstance(formValues, formInstanceMap, formInstances);
      
      // Prepare form_instances_data for submission and collect helper data
      const formInstancesData = {};

      formInstances.forEach(instance => {
        const instanceData = splitData[instance.id];

        if (instanceData) {
          const isHelperForm = (instance.instance_name || instance.master_form_name || '').includes('Helper Form');

          if (isHelperForm) {
            // Collect helper data to save at parent level
          } else {
            // Check for repeatable group data (sections with allows_multiple)
            const repeatableGroupKeys = Object.keys(instanceData).filter(k => k.includes('repeating_group_'));
            if (repeatableGroupKeys.length > 0) {
              console.log(`[NewStudentForm] Found repeatable group data in ${instance.instance_name || instance.master_form_name}:`, {
                keys: repeatableGroupKeys,
                sampleData: repeatableGroupKeys.map(k => ({ key: k, value: instanceData[k], isArray: Array.isArray(instanceData[k]), length: Array.isArray(instanceData[k]) ? instanceData[k].length : null }))
              });
            }
            
            // Regular form instance
            formInstancesData[instance.id] = {
              student_name: `${studentInfo.first_name} ${studentInfo.last_name}`,
              data: instanceData,
              allows_multiple: instance.form_data?.allows_multiple || false
            };
            
            console.log(`[NewStudentForm] Prepared form instance data for ${instance.instance_name || instance.master_form_name}:`, {
              instanceId: instance.id,
              dataKeys: Object.keys(instanceData),
              dataKeyCount: Object.keys(instanceData).length,
              hasRepeatableGroups: repeatableGroupKeys.length > 0,
              allows_multiple: instance.form_data?.allows_multiple || false
            });
          }
        }
      });

      // Submit to form-submissions endpoint (which will also create/update student)
      const submitData = {
        submission_data: sanitizeForJsonObject(formValues),
        form_instances_data: formInstancesData,
        student_info: studentInfo,
        form_type: 'new_student'
      };

      // Include student intake ID if available
      if (studentIntakeId) {
        submitData.student_intake_id = studentIntakeId;
      }


      await axios.post('/api/form-submissions/submit', submitData, { timeout: 30000 });

      // Then the DESE questionnaire, as its own record. Deliberately AFTER the
      // enrollment submit and in its own try: enrollment is already saved at this
      // point, so a questionnaire failure must not discard it or block the parent.
      // They are told, and can finish it from My Forms.
      if (studentIntakeId && !migrantAlreadyOnFile) {
        try {
          await axios.post('/api/migrant-questionnaire/submit', {
            studentIntakeId,
            answers: { ...migrantAnswers, language: migrantLanguage }
          }, { timeout: 30000 });
        } catch (mqErr) {
          console.error('Migrant questionnaire submit failed:', mqErr);
          setMigrantError(
            'Your enrollment forms were submitted, but the Migrant Education questionnaire did not save. ' +
            'You can complete it from the Forms page.'
          );
        }
      }

      // Mark this student as submitted to prevent showing empty form on refresh
      if (studentIntakeId) {
        sessionStorage.setItem(`student_submitted_${studentIntakeId}`, 'true');
      }

      // Clear saved progress after successful submission (non-blocking)
      // Don't wait for this to complete before navigating
      axios.post('/api/form-submissions/save-progress', {
        submission_data: {},
        progress_percentage: 0,
        student_intake_id: studentIntakeId, // Include student intake ID for clearing
        form_type: 'new_student'
      }).then(() => {
        // Notify parent dashboard about progress update (form completed)
        window.dispatchEvent(new CustomEvent('progressUpdated'));
      }).catch(error => {
        console.error('Failed to clear saved progress:', error);
        // Don't block navigation if this fails
      });

      // Clear form data immediately after successful submission
      setFormValues({});
      helperFormDataRef.current = {};
      setHelperFormData({});
      setHouseholdInformation({});
      setManuallyChangedHelperFields(new Set());
      setSuccess(true);
      setLoading(false); // Clear loading state to show success

      // Navigate with success message - use setTimeout to ensure state updates complete
      const studentName = `${studentInfo.first_name} ${studentInfo.last_name}`;
      setTimeout(() => {
        navigate(`/parent/student-intake?success=true&studentName=${encodeURIComponent(studentName)}`);
      }, 100);
    } catch (error) {
      console.error('Form submission error:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setError(error.response?.data?.error || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>New Student - {studentIntake?.first_name || 'Loading...'}</h2>
        <div>Loading forms...</div>
      </div>
    );
  }

  if (!combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>New Student - {studentIntake?.first_name || 'Loading...'}</h2>
        {error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div>No forms available</div>
        )}
      </div>
    );
  }

  return (
    <div className="add-district">
      <h2>New Student - {studentIntake?.first_name || 'Loading...'}</h2>

      {/* Progress Bar with Save Button */}
      <div style={{
        background: '#ff9800',
        color: 'white',
        padding: '0.75rem 1.5rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong>Form Progress:</strong> {calculateProgress()}% complete
          {lastSavedTime && (
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', opacity: 0.8 }}>
              Last saved: {lastSavedTime.toLocaleDateString()} {lastSavedTime.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => saveProgress({ trigger: 'manual' })}
          disabled={savingProgress}
          style={{
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid white',
            padding: '0.4rem 0.8rem',
            borderRadius: '4px',
            cursor: savingProgress ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem'
          }}
        >
          {savingProgress ? 'Saving...' : 'Save Progress'}
        </button>
      </div>

      {hasLoadedProgress && (
        <div style={{ 
          background: '#d1ecf1', 
          color: '#0c5460', 
          padding: '1rem', 
          borderRadius: '4px', 
          marginBottom: '1rem', 
          border: '1px solid #bee5eb',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>✓</span>
          <div>
            <strong>Saved Progress Loaded</strong> - Your previous form data has been restored. You can continue where you left off.
          </div>
        </div>
      )}
      {savingProgress && (
        <div style={{ background: '#e3f2fd', color: '#1976d2', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem' }}>
          Saving progress...
        </div>
      )}
      <SuccessPopup
        show={!!saveFeedbackModal}
        onClose={() => setSaveFeedbackModal(null)}
        variant={saveFeedbackModal?.variant || 'success'}
        title={saveFeedbackModal?.title}
        message={saveFeedbackModal?.message || ''}
        autoCloseDelay={saveFeedbackModal?.variant === 'error' ? 0 : 5000}
      />
      <AutoSaveToast
        open={!!autoSaveToast}
        onClose={() => setAutoSaveToast(null)}
        variant={autoSaveToast?.variant || 'success'}
        message={autoSaveToast?.message || ''}
        duration={5000}
      />
      {error && <div className="error-message">{error}</div>}
      {success && (
        <div style={{ background: '#dfd', color: '#3a3', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          Student information submitted successfully!
        </div>
      )}
      {siblingAutofillMessage && (
        <div style={{ background: '#f0f4ff', color: '#1c3d5a', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #c3d8ff' }}>
          {siblingAutofillMessage}
        </div>
      )}
      {(() => {
        const completionStatus = getFormCompletionStatus();
        if (!completionStatus.isComplete && Object.keys(formValues).length > 0) {
          return (
            <div style={{
              background: '#fff3cd',
              color: '#856404',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              border: '1px solid #ffc107'
            }}>
              <strong>Form Incomplete:</strong> Please fill in all required fields before submitting.
              {completionStatus.missingFieldDetails.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Missing required fields by section:</strong>
                  </div>
                  {completionStatus.missingFieldDetails.slice(0, 2).map((section, idx) => (
                    <div key={idx} style={{ marginBottom: '0.25rem' }}>
                      <strong>{section.sectionName}:</strong>{' '}
                      {section.fields.slice(0, 2).map((field, fieldIdx) => (
                        <button
                          key={fieldIdx}
                          type="button"
                          onClick={() => scrollToMissingField(field, section)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#007bff',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: '0',
                            fontSize: 'inherit'
                          }}
                        >
                          {field.type === 'map' ? `${field.label} (Find on Map / place marker)` : field.label}
                        </button>
                      ))}
                      {section.fields.length > 2 && ` (+${section.fields.length - 2} more)`}
                      {idx < completionStatus.missingFieldDetails.slice(0, 2).length - 1 && '; '}
                    </div>
                  ))}
                  {completionStatus.missingFieldDetails.length > 2 && (
                    <div>... and {completionStatus.missingFieldDetails.length - 2} more sections</div>
                  )}
                </div>
              )}
            </div>
          );
        }
        return null;
      })()}
      <form onSubmit={handleSubmit}>
        <FormRenderer
          formData={combinedFormDefinition}
          formValues={formValues}
          onChange={handleFormChange}
          errors={validationErrors}
          autofillEnabled={autofillEnabled}
          onAutofillToggle={(instanceId, enabled) => {
            setAutofillEnabled(prev => ({
              ...prev,
              [instanceId]: enabled
            }));
          }}
          userRole="parent"
          hideFormYear
          isFieldReadOnly={isFieldReadOnly}
          onAddSiblingsFromList={siblings.length > 0 ? handleAddFromStudentList : null}
        />
        {/* DESE Title I.C questionnaire, completed in this same flow so it actually gets
            done, and so the household block autofills instead of being retyped elsewhere. */}
        <div
          id="migrant-questionnaire-block"
          style={{
            marginTop: '2rem',
            padding: '1.25rem',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            background: '#fff'
          }}
        >
          {migrantAlreadyOnFile ? (
            <>
              <h3 style={{ marginTop: 0 }}>Migrant Education Parent Questionnaire</h3>
              <p style={{
                background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724',
                borderRadius: '4px', padding: '0.75rem', margin: 0
              }}>
                Already completed for this student - no need to answer again.
              </p>
            </>
          ) : (
            <MigrantQuestionnaire
              embedded
              studentIntakeId={studentIntakeId}
              answers={migrantAnswers}
              onAnswersChange={(next) => {
                setMigrantAnswers(next);
                // Clear the host's validation message too, not just the component's
                // own, so it does not sit there after the parent has answered.
                if (migrantError) setMigrantError('');
              }}
              language={migrantLanguage}
              onLanguageChange={setMigrantLanguage}
              externalError={migrantError}
            />
          )}
        </div>

        <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={loading || !isFormComplete() || migrantMissingKeys.length > 0}
              title={
                migrantMissingKeys.length > 0
                  ? 'Please answer the Migrant Education Questionnaire questions before submitting'
                  : (!isFormComplete() ? 'Please complete all required fields before submitting' : '')
              }
            >
              {loading ? 'Submitting...' : 'Submit All Forms'}
            </button>
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={async () => {
                await saveProgress({ trigger: 'manual' });
                navigate('/parent/student-intake');
              }}
            >
              Save for Later
            </button>
          </div>
          <button 
            type="button" 
            className="btn-danger" 
            onClick={() => setShowDeleteConfirm(true)}
            style={{ background: '#dc3545', color: 'white', border: 'none' }}
          >
            Cancel Form
          </button>
        </div>

        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}>
              <h3 style={{ marginTop: 0 }}>Delete Form Progress?</h3>
              <p style={{ marginBottom: '1rem' }}>
                Are you sure you want to delete all your form progress? This action cannot be undone.
              </p>
              <div style={{ 
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: '4px', 
                padding: '1rem', 
                marginBottom: '1rem' 
              }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
                  <strong>Tip:</strong> If you want to save your progress and continue later, 
                  just close this page or click "Save for Later". Your progress is automatically saved 
                  every 30 seconds and will be available when you return.
                </p>
              </div>
              <div className="modal-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowDeleteConfirm(false)} 
                  className="btn-secondary"
                >
                  Keep Progress
                </button>
                <button 
                  onClick={handleDeleteProgress} 
                  className="btn-danger"
                  style={{ background: '#dc3545', color: 'white', border: 'none' }}
                >
                  Delete Progress
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      {recoverableDraft && (
        <div className="nsf-recover" role="alert">
          <div className="nsf-recover-title">Unsaved answers found on this device</div>
          <p className="nsf-recover-body">
            {recoverableDraft.recoverable} answer{recoverableDraft.recoverable === 1 ? '' : 's'} you
            entered {describeAge(recoverableDraft.savedAt)} never reached us - the connection may
            have dropped. You can put them back, or carry on with what is on screen now.
            {recoverableDraft.droppedSignatureFields?.length > 0
              && ' Signatures are not included and will need signing again.'}
          </p>
          <div className="nsf-recover-actions">
            <button
              type="button"
              className="nsf-btn nsf-btn-submit"
              onClick={() => {
                setFormValues((prev) => ({ ...prev, ...recoverableDraft.values }));
                setRecoverableDraft(null);
                setAutoSaveToast({
                  variant: 'success',
                  message: 'Your unsaved answers were restored. Check them, then save.'
                });
              }}
            >
              Restore my answers
            </button>
            <button
              type="button"
              className="nsf-btn nsf-btn-top"
              onClick={() => { clearLocalDraft(studentIntakeId); setRecoverableDraft(null); }}
            >
              Discard them
            </button>
          </div>
        </div>
      )}

      {/*
        Fixed bottom bar. Progress spans the full width on its own row so the list of
        what is left gets the whole width underneath it, rather than being squeezed into
        60% beside the buttons. Styling moved to classes because the layout has to change
        on a phone and inline styles cannot be reached by a media query.
      */}
      {(() => {
        // Computed once here rather than four times through this block.
        const completion = getFormCompletionStatus();
        const { totalRequired = 0, answered = 0, percent = 0, isComplete } = completion;
        const remaining = completion.missingFields.length;

        return (
          <div className={`nsf-actionbar is-${barState}`}>
            {/* Swipe up for more room, down to shrink, tap to toggle. Also a plain
                button, because a hidden gesture is not a control. */}
            <div
              className="nsf-grab"
              onPointerDown={onGrabDown}
              onPointerUp={onGrabUp}
              role="separator"
              aria-label="Drag to resize, or tap to show and hide the list of remaining fields"
            >
              <span className="nsf-grab-bar" aria-hidden="true" />
            </div>
            {totalRequired > 0 && (
              <div className="nsf-progress" aria-live="polite">
                <div className="nsf-progress-head">
                  <span className="nsf-progress-count">{answered}/{totalRequired}</span>
                  <span className="nsf-progress-pct">{percent}%</span>
                </div>
                <div className="nsf-progress-spacer">
                </div>
                <div
                  className="nsf-progress-track"
                  role="progressbar"
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Enrollment form progress"
                >
                  <div
                    className={`nsf-progress-fill ${percent === 100 ? 'is-done' : ''}`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <button
                  type="button"
                  className="nsf-bar-toggle"
                  onClick={() => setBar(barState === 'min' ? 'default' : 'min')}
                  aria-expanded={barState !== 'min'}
                  title={barState === 'min' ? 'Show what is left to fill in' : 'Hide the list'}
                >
                  {barState === 'min' ? '▲' : '▼'}
                </button>
              </div>
            )}

            <div className="nsf-actionbar-row">
              <div className="nsf-actionbar-missing">
                {remaining > 0 ? (
                  <>
                    <div className="nsf-missing-head">{remaining} still to fill in</div>
                    {/* Chips, not a run-on sentence: each is its own tap target, long
                        labels truncate rather than pushing the rest off screen, and the
                        full wording stays available on hover. */}
                    <div className="nsf-missing-chips">
                      {completion.missingFieldDetails.map((section, idx) => (
                        section.fields.slice(0, 4).map((field, fieldIdx) => (
                          <button
                            key={`${idx}-${fieldIdx}`}
                            type="button"
                            className="nsf-chip"
                            onClick={() => scrollToMissingField(field, section)}
                            title={`${section.sectionName}: ${field.label}${field.type === 'map' ? ' (place a marker on the map)' : ''}`}
                          >
                            {field.type === 'map' ? `${field.label} (map)` : field.label}
                          </button>
                        ))
                      ))}
                      {completion.missingFields.length > completion.missingFieldDetails.reduce((n, sec) => n + Math.min(sec.fields.length, 4), 0) && (
                        <span className="nsf-chip-more">
                          +{completion.missingFields.length - completion.missingFieldDetails.reduce((n, sec) => n + Math.min(sec.fields.length, 4), 0)} more
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="nsf-missing-done">✓ Everything required is filled in</div>
                )}
              </div>

              <div className="nsf-actionbar-buttons">
                <button
                  type="button"
                  onClick={() => saveProgress({ trigger: 'manual' })}
                  disabled={savingProgress}
                  className="nsf-btn nsf-btn-save"
                >
                  {savingProgress ? 'Saving...' : 'Save Progress'}
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !isComplete}
                  className={`nsf-btn nsf-btn-submit ${isComplete ? '' : 'is-disabled'}`}
                  title={!isComplete ? 'Please complete all required fields before submitting' : ''}
                >
                  {loading ? 'Submitting...' : 'Submit Form'}
                </button>

                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="nsf-btn nsf-btn-top"
                  title="Scroll to top of the page"
                >
                  ↑ Top
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add padding to main content to prevent overlap with fixed bottom bar */}
      <div style={{ height: '100px' }}></div>
    </div>
  );
};

export default NewStudentForm;
